/**
 * simpleCsvImporter.js
 *
 * Einfacher, robuster CSV-Importer für Krypto-Exchanges.
 * Alles in einer Datei — kein Over-Engineering, leicht erweiterbar.
 *
 * Unterstützte Exchanges: Binance, Kraken, Coinbase, Bitget, Bitvavo, Bitpanda, Gemini, Ledger, MoonPay
 * Fallback: Generic Parser (heuristisch)
 *
 * Einziger Export: importCsvFile(file) → Promise<ImportResult>
 *
 * ImportResult:
 * {
 *   exchange:  string,         // erkannte Börse oder 'Generic'
 *   txs:       Transaction[],  // erfolgreich geparste Transaktionen (kanonisches Format)
 *   failed:    FailedRow[],    // Zeilen die fehlgeschlagen sind mit Grund
 *   warnings:  string[],       // allgemeine Warnungen (z.B. unbekannte Typen)
 *   stats: {
 *     totalRows:   number,     // Gesamtanzahl Datenzeilen
 *     parsed:      number,     // erfolgreich geparste Zeilen
 *     skipped:     number,     // übersprungene Zeilen (z.B. Leerzeilen)
 *     failed:      number,     // fehlgeschlagene Zeilen
 *   }
 * }
 */

import { annotateTx, markCollisions } from './txFingerprint';
import { convertFiatPricesToEur } from './fxConvert';

// ── Parser-Version ──────────────────────────────────────────────────────────
//
// Jede importierte TX bekommt einen `parserVersion`-Stempel. Wird die Logik
// hier geändert (Fee-Handling, Pair-Logik, neue Operation-Typen, …) → Version
// erhöhen. Der ImportReviewScreen erkennt dann bestehende TXs mit kleinerer
// Version (oder ganz ohne Stempel = pre-versioning) als „stale" und bietet
// einen Replace-and-Reimport-Flow an, statt der User durch den Dedup-Dance
// laufen zu lassen.
//
// Historie der Versionssprünge:
//   1  (initial) — Basis. Hatte Bug bei Multi-Trade-Sekundengruppen (z.B.
//                  zwei SHIB-Verkäufe am 2021-10-28 12:59:03 → nur einer
//                  importiert), Binance Transaction-Related-Format teilweise
//                  als unknown verworfen, Fee-In-Coin nicht zentral.
//   2  (2026-05) — applyTradingFee-Architektur, vollständige Pair-Logik
//                  Sold/Revenue + Spend/Buy, Stablecoin-Resolver, Fiat-Quote-
//                  Direkt-Bewertung, Transaction-Sold/Buy/Spend/Revenue/Related
//                  durchgängig erfasst. Binance-CSV-Bilanztest verifiziert
//                  SHIB ≈ 0.55, BUSD ≈ 0, USDC ≈ 0.66.
//   3  (2026-05) — Binance „Withdraw" und „Deposit" werden als
//                  externalTransfer=true geflaggt. Verhindert Phantom-Lots
//                  (z.B. 8.623 XRP-Withdraw blieb in der FIFO hängen wenn die
//                  Gegenseite nicht importiert war). transferMatcher pairt
//                  weiterhin bei passender Gegenseite — matchedOutIds gewinnt
//                  vor externalTransfer in fifo.js.
//   4  (2026-05) — externalTransfer wird jetzt im App.js-Mapping korrekt nach
//                  Firestore durchgereicht (vorher ging das Flag in der
//                  Row→TX-Konvertierung verloren). BTT-Rebranding zeigt jetzt
//                  „Externe Wallet" statt „?", XRP/XLM-Withdraws konsumieren
//                  die Lots auch in der echten App (nicht nur im Test).
//   5  (2026-05) — Coinbase „Convert" wird korrekt als zweiseitiger Swap
//                  geparst (Notes „Converted X SRC to Y DST"). Vorher entstand
//                  ein einseitiger Swap (coinOut/coinIn=null) → App-Crash
//                  (schwarzer Bildschirm) + invertierte Anzeige. Coinbase
//                  Send/Receive jetzt als externalTransfer geflaggt.
//   6  (2026-05) — parseAmount entfernt Währungssymbole (€/$/£) + ISO-Suffixe.
//                  Coinbase EUR-Beträge haben führendes €-Zeichen ("-€92.38")
//                  → parseFloat=NaN→0 → alle Preise 0, Käufe scheiterten an
//                  "Preis fehlt". Jetzt korrekt geparst.
//   7  (2026-05) — parseDateTime rechnet Zeitzonen-Offset (+02:00) auf UTC um.
//                  Bitpanda exportiert Lokalzeit mit Offset, Ledger UTC. Ohne
//                  Umrechnung waren Bitpanda-Zeiten 1-2h zu spät → Transfer-
//                  Matcher fand Ledger-Gegenstücke nicht (1h-Limit) → XLM/XRP
//                  Doppelzählung (5081 statt 2496). Sommerzeit (+02) traf's
//                  härter als Winter (+01) — daher matchte 683 aber 492/1319 nicht.
//   8  (2026-05) — Bitvavo "Vollständige Transaktionshistorie" (kein Market-Feld,
//                  Spalte "Received / Paid Amount"). Wurde als Generic erkannt
//                  → 0€-Preise, 7 Fehler. Neuer parseBitvavoFullHistory: preis =
//                  |Received/Paid Amount| (realer EUR-Cashflow, Fee enthalten),
//                  gebuehr=0. XRP/XLM netten innerhalb Bitvavo auf 0.
//   9  (2026-06) — Generic-Parser robuster gegen handgepflegte Tabellen
//                  (Stresstest manualImport.test.js): (A) deutsche Typ-Wörter
//                  „Einzahlung/Auszahlung/Gebühr/gestohlen/verloren" werden
//                  erkannt (waren vorher „unknown" → Zeile still verworfen).
//                  (B) parseAmount deutet „12.500.000" (≥2 Tausenderpunkte, kein
//                  Komma) als 12500000 statt 12.5 — einzelner Punkt bleibt
//                  Dezimaltrenner (Binance-Decimals unberührt). (C) einzeiliger
//                  „Tausch" baut einen echten Swap aus „Ziel-Coin"/„Ziel-Menge"-
//                  Spalten ODER wird mit Warnung übersprungen statt einen
//                  beinlosen (coinIn/coinOut=null) Swap zu erzeugen. (D) menge=0
//                  erzeugt jetzt eine Warnung statt stillem Verwerfen.
export const PARSER_VERSION = 9;

// ── Generische Trading-Fee-Anwendung ────────────────────────────────────────
// Viele Börsen ziehen Trading-Fees direkt in der erhaltenen oder abgegebenen
// Coin ab und liefern sie als separate CSV-Zeile (Binance Transaction Fee,
// Trade-History "Fee" mit Coin-Suffix usw.). Ohne Korrektur erzeugen wir eine
// eigenständige gebuehr-TX nebst voller Brutto-Menge im Trade → Bestand zu hoch.
//
// Logik:
//   Fee-Coin == empfangene Coin  → menge/mengeIn reduzieren (du bekommst weniger)
//   Fee-Coin == abgegebene Coin  → menge/mengeOut erhöhen   (du gibst mehr ab)
//   Fee-Coin == Fiat             → tx.gebuehr (EUR-Kostenbasis)
//   Fee-Coin == dritte Coin      → null zurück → Aufrufer emittiert separate gebuehr-TX
const FEE_FIAT_SYMS = new Set(['EUR', 'USD', 'GBP', 'CHF', 'AUD', 'CAD', 'JPY', 'TRY', 'BRL']);

// Baut eine Fiat-Ein-/Auszahlungs-TX (EUR/USD … Bank↔Börse). Diese sind KEIN
// Krypto-Bestandsereignis — sie werden downstream konsequent über coin∈FIAT aus
// Portfolio (PORTFOLIO_FIAT_EXCLUDE), FIFO und Transfer-Matching ausgeschlossen.
// Aber sie sind ein REALER Geldfluss, den der User sehen will. Wir verwerfen sie
// daher NICHT mehr (führte zu „Einzahlung taucht gar nicht auf"), sondern führen
// sie als eigene Kategorie in der Transaktionsliste.
// Richtung: explizites `typ` (einzahlung/auszahlung) gewinnt, sonst Vorzeichen
// von `amount` (>=0 = Einzahlung). `eurValue` = optionaler Fiat-Gegenwert für die
// Anzeige; bei EUR ist die Menge selbst der EUR-Wert.
function buildFiatTx(sym, amount, { datum, zeit, exchange, notiz, eurValue, typ } = {}) {
  const menge = Math.abs(amount);
  const t = (typ === 'einzahlung' || typ === 'auszahlung')
    ? typ
    : (amount >= 0 ? 'einzahlung' : 'auszahlung');
  const ev = eurValue != null ? Math.abs(eurValue) : null;
  return {
    typ: t,
    sym,
    datum, zeit,
    menge,
    preis: ev != null && ev > 0 ? ev : (String(sym).toUpperCase() === 'EUR' ? menge : 0),
    gebuehr: 0,
    exchange,
    notiz: notiz || (t === 'einzahlung' ? 'Fiat-Einzahlung' : 'Fiat-Auszahlung'),
  };
}

function applyTradingFee(tx, feeCoinRaw, feeAmount) {
  if (!tx || !(feeAmount > 0) || !feeCoinRaw) return false;
  const feeCoin = String(feeCoinRaw).trim().toUpperCase();

  const buyCoin  = tx.typ === 'swap' ? tx.symIn  : (tx.typ === 'kauf'    ? tx.sym : null);
  const sellCoin = tx.typ === 'swap' ? tx.symOut : (tx.typ === 'verkauf' ? tx.sym : null);

  // _fee = Original-Gebuehr-Metadaten (fuer Edit-Formular + Transparenz im UI).
  // Wird NICHT fuer FIFO/Reporting genutzt — dort zaehlt weiterhin tx.gebuehr
  // bzw. die bereits angepasste menge. Nur Anzeige.
  const recordFeeMeta = (absorbed) => {
    tx._fee = { amount: feeAmount, coin: feeCoin, absorbed };
  };

  if (buyCoin && feeCoin === buyCoin) {
    if (tx.typ === 'swap') tx.mengeIn = Math.max(0, (tx.mengeIn || 0) - feeAmount);
    else                   tx.menge   = Math.max(0, (tx.menge   || 0) - feeAmount);
    recordFeeMeta('menge_reduced');
    return true;
  }
  if (sellCoin && feeCoin === sellCoin) {
    if (tx.typ === 'swap') tx.mengeOut = (tx.mengeOut || 0) + feeAmount;
    else                   tx.menge    = (tx.menge    || 0) + feeAmount;
    recordFeeMeta('menge_increased');
    return true;
  }
  if (FEE_FIAT_SYMS.has(feeCoin)) {
    tx.gebuehr = (tx.gebuehr || 0) + feeAmount;
    recordFeeMeta('gebuehr');
    return true;
  }
  return false; // Fee in dritter Coin → Aufrufer emittiert separate gebuehr-TX
}

// ─────────────────────────────────────────────────────────────────────────────
// ABSCHNITT 1: Hilfsfunktionen (Parsing)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Liest eine Browser-File als Text (UTF-8, BOM-sicher).
 */
function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      let text = e.target.result;
      // BOM (Byte Order Mark) entfernen
      if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
      resolve(text);
    };
    reader.onerror = () => reject(new Error(`Datei konnte nicht gelesen werden: ${file.name}`));
    reader.readAsText(file, 'UTF-8');
  });
}

/**
 * Parst Zahl aus String — unterstützt US- und EU-Format (Komma/Punkt).
 * Gibt 0 zurück wenn unparsebar.
 */
function parseAmount(raw) {
  if (raw === undefined || raw === null || raw === '') return 0;
  let s = String(raw).trim().replace(/\s/g, '').replace(/^\+/, '');
  // Währungssymbole + ISO-Codes entfernen — Coinbase exportiert EUR-Beträge mit
  // führendem €-Zeichen ("-€92.38"), manche Exporte mit "EUR"/"USD"-Suffix.
  // Ohne Strip wird parseFloat("-€92.38") = NaN → 0 (alle Preise verschwinden,
  // Käufe scheitern an "Preis fehlt"). Kein echter Zahlenwert enthält diese.
  s = s.replace(/[€$£¥₿]/g, '').replace(/(EUR|USD|GBP|CHF|USDT|USDC)$/i, '');

  const hasComma = /,/.test(s);
  const hasDot = /\./.test(s);

  if (hasComma && hasDot) {
    // Beides vorhanden → das LETZTE Zeichen ist der Dezimaltrenner.
    if (s.lastIndexOf(',') > s.lastIndexOf('.')) {
      // EU: 1.234,56 → 1234.56
      s = s.replace(/\./g, '').replace(',', '.');
    } else {
      // US: 1,234.56 → 1234.56
      s = s.replace(/,/g, '');
    }
  } else if (hasComma) {
    // Nur Komma → EU-Dezimal (12,34) ODER US-Tausender (1,234)
    // Tausender-Heuristik: genau Gruppen aus 3 Ziffern getrennt durch Komma.
    if (/^\d{1,3}(,\d{3})+$/.test(s)) {
      s = s.replace(/,/g, '');
    } else {
      s = s.replace(',', '.');
    }
  } else if (hasDot) {
    // Nur Punkt(e), kein Komma. EIN Punkt = Dezimaltrenner — "46.964" bleibt
    // 46.964 (Binance-Decimals dürfen NICHT zerstört werden). ZWEI ODER MEHR
    // Punkte können kein US-Dezimal sein (ein Dezimal hat höchstens einen Punkt)
    // → das ist deutscher Tausenderpunkt ("12.500.000" = 12500000). Streng auf
    // 3er-Gruppen begrenzt, damit nur eindeutige Fälle umgedeutet werden.
    if (/^\d{1,3}(\.\d{3}){2,}$/.test(s)) {
      s = s.replace(/\./g, '');
    }
  }
  // Sonst (ein Punkt oder gar nichts): als US-Dezimal interpretieren (46.964).

  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

/**
 * Parst Datum + Uhrzeit aus einem String.
 * Gibt { datum: 'YYYY-MM-DD', zeit: 'HH:mm:ss' | null } zurück.
 */
function parseDateTime(raw) {
  if (!raw) return { datum: null, zeit: null };
  const s = String(raw).trim();

  // ISO mit Zeit: 2023-04-15T14:30:00Z / 2023-04-15 14:30:00 / mit Offset +02:00
  const isoFull = s.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})(\.\d+)?(Z|[+-]\d{2}:?\d{2})?/);
  if (isoFull) {
    const tz = isoFull[4];
    // Zeitzonen-Offset (+02:00 / Z) → auf UTC umrechnen. KRITISCH fuer
    // Cross-Exchange-Transfer-Matching: Bitpanda exportiert Lokalzeit mit Offset
    // ("2025-04-01T17:31:39+02:00"), Ledger exportiert UTC ("...Z"). Ohne
    // Umrechnung wuerde Bitpanda-Zeit 1-2h zu spaet gespeichert → der
    // transferMatcher (1h-Rueckwaertslimit) findet das Ledger-Gegenstueck nicht
    // mehr → Doppelzaehlung (z.B. XLM 5081 statt 2496). Mit Z/UTC unveraendert.
    if (tz && tz !== 'Z') {
      const d = new Date(s);
      if (!Number.isNaN(d.getTime())) {
        return { datum: d.toISOString().slice(0, 10), zeit: d.toISOString().slice(11, 19) };
      }
    }
    return { datum: isoFull[1], zeit: isoFull[2] };
  }

  // ISO nur Datum: 2023-04-15
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return { datum: s, zeit: null };

  // Binance Kurzformat: YY-MM-DD HH:MM:SS  (z.B. "21-08-22 13:13:48")
  const shortYear = s.match(/^(\d{2})-(\d{2})-(\d{2})\s+(\d{2}:\d{2}:\d{2})/);
  if (shortYear) {
    const yy = parseInt(shortYear[1], 10);
    // 00-49 → 2000-2049, 50-99 → 1950-1999 (Binance gibt es erst seit 2017)
    const fullYear = yy < 50 ? 2000 + yy : 1900 + yy;
    return {
      datum: `${fullYear}-${shortYear[2]}-${shortYear[3]}`,
      zeit: shortYear[4],
    };
  }

  // DD.MM.YYYY oder DD/MM/YYYY mit Zeit
  const dmyFull = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})\s+(\d{2}:\d{2}:\d{2})/);
  if (dmyFull) {
    return {
      datum: `${dmyFull[3]}-${dmyFull[2].padStart(2, '0')}-${dmyFull[1].padStart(2, '0')}`,
      zeit: dmyFull[4],
    };
  }

  // DD.MM.YYYY oder DD/MM/YYYY ohne Zeit
  const dmy = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})/);
  if (dmy) {
    return {
      datum: `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`,
      zeit: null,
    };
  }

  // MM/DD/YYYY mit Zeit (US-Format)
  const mdyFull = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{2}:\d{2}:\d{2})/);
  if (mdyFull) {
    const m = parseInt(mdyFull[1], 10);
    // Erster Wert > 12 → muss Tag sein (DD/MM)
    const datum =
      m > 12
        ? `${mdyFull[3]}-${mdyFull[2].padStart(2, '0')}-${mdyFull[1].padStart(2, '0')}`
        : `${mdyFull[3]}-${mdyFull[1].padStart(2, '0')}-${mdyFull[2].padStart(2, '0')}`;
    return { datum, zeit: mdyFull[4] };
  }

  // Fallback: native Date-Parser
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    return {
      datum: d.toISOString().slice(0, 10),
      zeit: d.toISOString().slice(11, 19),
    };
  }

  return { datum: null, zeit: null };
}

/**
 * Normalisiert Asset-Symbole (UPPERCASE + bekannte Aliases).
 * WBTC, stETH etc. bleiben eigenständig (separates steuerliches Asset).
 */
function normalizeAsset(raw) {
  if (!raw) return '';
  return String(raw)
    .trim()
    .toUpperCase()
    .replace(/^([A-Z0-9]+)\.S$/, '$1')  // Kraken Staking-Suffix: ETH.S → ETH
    .replace(/^LD(.+)$/, '$1')           // Binance Locked Earn: LDBTC → BTC
    .replace(/^XBT$/, 'BTC')             // XBT = BTC (ISO 4217 Alias)
    .replace(/^MIOTA$/, 'IOTA');         // MIOTA = IOTA
}

/**
 * Liefert den Wert einer CSV-Spalte anhand mehrerer möglicher Namen (case-insensitive).
 * Leerzeichen und Sonderzeichen im Header werden toleriert.
 */
function col(row, headers, ...candidates) {
  for (const candidate of candidates) {
    const needle = candidate.toLowerCase().trim();
    const key = headers.find((h) => h.toLowerCase().trim() === needle);
    if (key !== undefined && row[key] !== undefined && row[key] !== '') {
      return row[key];
    }
  }
  return undefined;
}

// Fuzzy-Spaltensuche: erstes Header, das ALLE Substrings (lowercase) enthält.
// Für Header-Varianten, bei denen das exakte col() scheitert — z.B. Ledger-
// Exporte, deren Gegenwert-Spalte nicht exakt „Countervalue at Operation Date"
// heißt (Suffixe, Lokalisierung, andere Ledger-Live-Version). Die Substring-Kombi
// muss eindeutig sein (z.B. countervalue+operation trifft NICHT die Export-Spalte).
function colFuzzy(row, headers, ...needles) {
  const ns = needles.map((n) => n.toLowerCase());
  const key = headers.find((h) => {
    const hl = h.toLowerCase();
    return ns.every((n) => hl.includes(n));
  });
  return key !== undefined && row[key] !== undefined && row[key] !== '' ? row[key] : undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// ABSCHNITT 2: Typ-Mapping (CSV-Rohtexte → kanonische Typen)
// ─────────────────────────────────────────────────────────────────────────────

// Alle bekannten Rohwerte → kanonischer Typ.
// Erweiterbar: einfach neuen Key:Value eintragen.
const TYPE_MAP = {
  // Deutsch
  kauf: 'kauf', kaufen: 'kauf',
  verkauf: 'verkauf', verkaufen: 'verkauf',
  tausch: 'swap',

  // Englisch (universal)
  buy: 'kauf', purchase: 'kauf',
  sell: 'verkauf', sale: 'verkauf',
  swap: 'swap', trade: 'swap', convert: 'swap', conversion: 'swap',

  deposit: 'einzahlung', receive: 'einzahlung', received: 'einzahlung',
  empfangen: 'einzahlung', 'transfer in': 'einzahlung', 'crypto deposit': 'einzahlung',
  'fiat deposit': 'einzahlung', einzahlung: 'einzahlung', einzahlen: 'einzahlung',
  eingang: 'einzahlung', gutschrift: 'einzahlung', erhalten: 'einzahlung',

  withdrawal: 'auszahlung', withdraw: 'auszahlung', send: 'auszahlung', sent: 'auszahlung',
  senden: 'auszahlung', 'transfer out': 'auszahlung', 'crypto withdrawal': 'auszahlung',
  'fiat withdrawal': 'auszahlung', 'fiat withdraw': 'auszahlung', auszahlung: 'auszahlung',
  auszahlen: 'auszahlung', ausgang: 'auszahlung', abhebung: 'auszahlung', gesendet: 'auszahlung',

  bridge: 'bridge',

  staking: 'staking', 'staking reward': 'staking', 'staking rewards': 'staking',
  'staking income': 'staking', 'pos validator reward': 'staking',
  'simple earn locked rewards': 'staking', 'eth 2.0 staking rewards': 'staking',
  'launchpool interest': 'staking', 'rewards income': 'staking',

  airdrop: 'airdrop', 'airdrop reward': 'airdrop', distribution: 'airdrop',
  'learning reward': 'airdrop', 'airdrop assets': 'airdrop', 'crypto box': 'airdrop',

  mining: 'mining', 'mining reward': 'mining', 'mining income': 'mining',

  cashback: 'cashback', reward: 'cashback', 'crypto cashback': 'cashback',
  'binance card cashback': 'cashback',

  interest: 'interest', zinsen: 'interest', 'savings interest': 'interest',
  'earn interest': 'interest', 'simple earn flexible interest': 'interest',
  'flexible savings interest': 'interest', 'locked savings interest': 'interest',

  lending: 'lending', 'lending income': 'lending',
  farming: 'farming', 'liquidity mining': 'farming',

  fee: 'gebuehr', fees: 'gebuehr', 'network fee': 'gebuehr', 'transaction fee': 'gebuehr',
  'gebühr': 'gebuehr', gebuehr: 'gebuehr', 'gebühren': 'gebuehr', 'netzwerkgebühr': 'gebuehr',

  // Binance-spezifisch
  'small assets exchange bnb': 'swap', 'transaction buy': 'kauf',
  'transaction spend': 'swap', 'transaction revenue': 'einzahlung',
  'transaction sold': 'verkauf', 'binance convert': 'swap',

  // Kraken-spezifisch
  earn: 'staking', transfer: 'einzahlung', spend: 'swap',

  // Coinbase-spezifisch
  'advanced trade buy': 'kauf', 'advanced trade sell': 'verkauf',
  'coinbase earn': 'airdrop',

  // Ledger-spezifisch (Operation Type) — Ledger Live liefert die Klassifizierung
  // direkt mit: REWARD = Staking-/Delegations-Belohnung (Einkommen), nicht
  // „externer Empfang". Ohne diese Zeile fielen die 18 HBAR-Rewards durch.
  in: 'einzahlung', out: 'auszahlung', reward: 'staking',

  // Bitget-spezifisch
  'spot trade': 'swap', 'p2p buy': 'kauf', 'p2p sell': 'verkauf',
  'futures trade': 'futures_close', 'cross margin': 'margin', 'isolated margin': 'margin',

  // Futures / Derivate (Binance, Bitget, Bybit, OKX)
  'futures order': 'futures_close', 'futures open': 'futures_open',
  'futures close': 'futures_close', 'futures exit': 'futures_close',
  'realize pnl': 'futures_pnl', 'realized pnl': 'futures_pnl',
  'futures pnl': 'futures_pnl', 'usd-m futures': 'futures_pnl',
  'coin-m futures': 'futures_pnl', 'perpetual': 'futures_pnl',
  'funding fee': 'funding_fee', 'funding payment': 'funding_fee',
  liquidation: 'liquidation', 'forced liquidation': 'liquidation',
  margin: 'margin', 'margin trade': 'margin',
  'commission rebate': 'cashback',

  // Coinbase Pro / Advanced
  'pro fee': 'gebuehr', 'advanced trade': 'swap',

  // Verlust / Diebstahl
  loss: 'loss', stolen: 'loss', lost: 'loss', hack: 'loss',
  diebstahl: 'loss', verlust: 'loss', gestohlen: 'loss', verloren: 'loss',

  // NFT
  'nft buy': 'nft_buy', 'nft purchase': 'nft_buy',
  'nft sell': 'nft_sell', 'nft sale': 'nft_sell',

  burn: 'burn', mint: 'mint',
};

function mapType(rawType) {
  if (!rawType) return 'unknown';
  const key = String(rawType).toLowerCase().trim();
  return TYPE_MAP[key] ?? 'unknown';
}

// ─────────────────────────────────────────────────────────────────────────────
// ABSCHNITT 3: CSV-Text → Rows (Kern-Parser)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parst CSV-Rohtext in { headers, rows }.
 * Unterstützt: Komma, Semikolon, Tab als Trennzeichen.
 * Preamble-Zeilen (z.B. Bitpanda Metadaten) werden übersprungen.
 */
function parseCSVText(text) {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');

  // Erste Zeile suchen die wie ein Header aussieht: beginnt mit Buchstabe/Quote
  // UND hat genug Trennzeichen (>= 3) um eine echte Tabelle zu sein.
  // Reine Buchstaben-Heuristik reichte nicht: Bitpanda-Preamble (Disclaimer,
  // Name, E-Mail, "Account opened at:…", "Venue: Bitpanda") besteht komplett
  // aus solchen Zeilen — der Disclaimer wurde als Header missinterpretiert,
  // Bitpanda als 'Generic' verworfen, und der Import landete im KI-Fallback.
  const looksLikeHeader = (l) => {
    const t = l.trim();
    if (!/^["']?[A-Za-z]/.test(t)) return false;
    const sepCount = Math.max(
      (t.match(/,/g)  || []).length,
      (t.match(/;/g)  || []).length,
      (t.match(/\t/g) || []).length,
    );
    return sepCount >= 3;
  };
  const dataStart = lines.findIndex(looksLikeHeader);
  if (dataStart === -1) return { headers: [], rows: [], rawLines: [] };

  const relevant = lines.slice(dataStart).filter((l) => l.trim() !== '');
  if (relevant.length < 2) return { headers: [], rows: [], rawLines: [] };

  // Trennzeichen ermitteln (häufigster von ,  ;  Tab in der ersten Zeile)
  const sample = relevant[0];
  const counts = {
    ',': (sample.match(/,/g) || []).length,
    ';': (sample.match(/;/g) || []).length,
    '\t': (sample.match(/\t/g) || []).length,
  };
  const sep = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];

  console.log(`[CSV] Trennzeichen erkannt: ${sep === '\t' ? 'Tab' : `"${sep}"`}`);

  // RFC 4180-kompatibler Split (berücksichtigt Anführungszeichen)
  const splitLine = (line) => {
    const result = [];
    let cur = '';
    let inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQuote && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuote = !inQuote;
        }
      } else if (c === sep && !inQuote) {
        result.push(cur.trim());
        cur = '';
      } else {
        cur += c;
      }
    }
    result.push(cur.trim());
    return result;
  };

  const headers = splitLine(relevant[0]);
  const rawLines = relevant.slice(1);
  const rows = rawLines
    .filter((l) => l.trim() !== '')
    .map((l, idx) => {
      const vals = splitLine(l);
      const obj = { _lineIndex: dataStart + 1 + idx, _rawCsvRow: l }; // für Fehlermeldungen
      headers.forEach((h, i) => {
        obj[h] = vals[i] ?? '';
      });
      return obj;
    });

  return { headers, rows, rawLines };
}

// ─────────────────────────────────────────────────────────────────────────────
// ABSCHNITT 4: Exchange-Erkennung
// ─────────────────────────────────────────────────────────────────────────────

const EXCHANGE_SIGNATURES = [
  {
    name: 'Binance',
    // Drei Formate:
    //   A) Tx-History (alt): UTC_Time + Operation
    //   B) Tx-History (neu): User ID + Time + Operation + Coin + Change
    //   C) Trade-History: Date(UTC) + Pair + Side
    detect: (h) =>
      (h.includes('UTC_Time') && h.includes('Operation')) ||
      (h.includes('Date(UTC)') && h.includes('Pair') && h.includes('Side')) ||
      (h.includes('User_ID') && h.includes('UTC_Time')) ||
      ((h.includes('User ID') || h.includes('User_ID')) &&
        h.includes('Operation') &&
        h.includes('Coin') &&
        h.includes('Change')),
  },
  {
    name: 'Kraken',
    detect: (h) => h.includes('txid') && h.includes('refid') && h.includes('aclass'),
  },
  {
    name: 'Coinbase',
    detect: (h) =>
      (h.includes('Transaction Type') && h.includes('Asset') && h.includes('Quantity Transacted')) ||
      (h.includes('Timestamp') && h.includes('Transaction Type') && h.includes('Spot Price Currency')),
  },
  {
    // Bitget USDT-M / Coin-M Futures: "Futures" + "Margin Mode" + "Wallet balance" sind eindeutig
    name: 'BitgetFutures',
    detect: (h) =>
      h.some(x => x.toLowerCase() === 'futures') &&
      h.some(x => x.toLowerCase() === 'margin mode') &&
      h.some(x => x.toLowerCase().replace(/\s/g, '') === 'walletbalance'),
  },
  {
    // Bitget Spot Account Statement: order,Date,Coin,Type,Amount,Fee,Available
    name: 'BitgetSpot',
    detect: (h) =>
      h.some(x => x.toLowerCase() === 'order') &&
      h.some(x => x === 'Date') &&
      h.some(x => x === 'Coin') &&
      h.some(x => x === 'Type') &&
      h.some(x => x === 'Amount') &&
      h.some(x => x === 'Available'),
  },
  {
    name: 'Bitget',
    detect: (h) =>
      (h.includes('orderId') || h.includes('orderNo')) &&
      (h.includes('fillPrice') || h.includes('quoteVolume')),
  },
  {
    name: 'Bitvavo',
    detect: (h) =>
      // Trade-Export (mit Market-Spalte)
      (h.includes('Market') &&
        (h.includes('Side') || h.includes('Type')) &&
        h.includes('Amount') &&
        h.includes('Fee amount'))
      // Vollständige Transaktionshistorie (Type/Currency/Quote Price/Received-Paid)
      || (h.includes('Type') &&
        h.includes('Currency') &&
        h.includes('Quote Price') &&
        h.includes('Received / Paid Amount')),
  },
  {
    name: 'Bitpanda',
    detect: (h) =>
      // Altes Format (vor 2025)
      (h.includes('Transaction ID') && h.includes('Type') && h.includes('Cryptocoin')) ||
      h.includes('BEST Fee') ||
      h.includes('Cryptocoin Symbol') ||
      // Neues Format (ab 2025): 'Transaction Type' + 'Amount Asset' + 'Asset market price'
      (h.includes('Transaction ID') && h.includes('Transaction Type') && h.includes('Amount Asset')) ||
      (h.includes('Transaction ID') && h.includes('Asset market price')),
  },
  {
    name: 'Gemini',
    detect: (h) => h.includes('Symbol') && h.includes('Specification') && h.includes('USD Amount USD'),
  },
  {
    // Ledger Live: "Currency Ticker" + "Operation Hash" sind eindeutig
    name: 'Ledger',
    detect: (h) =>
      h.includes('Currency Ticker') && (h.includes('Operation Hash') || h.includes('Operation Type')),
  },
  {
    // MoonPay trade_history.csv: baseCurrency/quoteCurrency-Paar + feeAmount sind eindeutig
    name: 'MoonPay',
    detect: (h) =>
      h.includes('baseCurrency') &&
      h.includes('baseCurrencyAmount') &&
      h.includes('quoteCurrency') &&
      h.includes('quoteCurrencyAmount'),
  },
  {
    // Bison / Börse Stuttgart Digital Exchange (BSDEX) transactionhistory.csv:
    // semikolon-getrennt. "Asset (amount)" + "Asset (market price)" + "Transaction type"
    // sind eindeutig. "Currency" = Fiat-Seite (Eur), "Asset" = Krypto-Symbol.
    name: 'Bison',
    detect: (h) =>
      h.includes('Transaction type') &&
      h.includes('Asset (amount)') &&
      h.includes('Asset (market price)'),
  },
  {
    // Crypto.com App export: "Transaction Kind" + "Native Amount" + "Transaction
    // Description" sind eindeutig für das App-CSV (nicht zu verwechseln mit der
    // Crypto.com Exchange, die ein anderes Format hat).
    name: 'CryptoCom',
    detect: (h) =>
      h.includes('Transaction Kind') &&
      h.includes('Transaction Description') &&
      (h.includes('Native Amount') || h.includes('Native Currency')),
  },
  {
    // KuCoin Spot Orders / Filled Orders: Symbol("BTC-USDT") + Side + Filled
    // Amount + Filled Volume + Fee Currency. "Filled Time(UTC…)" ist dynamisch.
    name: 'KuCoinSpot',
    detect: (h) =>
      h.includes('Symbol') &&
      h.includes('Side') &&
      h.includes('Filled Amount') &&
      h.includes('Filled Volume') &&
      h.includes('Fee Currency'),
  },
  {
    // KuCoin Deposit/Withdrawal History: Coin + Amount + ein "Time(UTC…)"-Header
    // + Transfer Network / Adress-Spalte. Status-gefiltert auf SUCCESS.
    name: 'KuCoinTransfer',
    detect: (h) =>
      h.includes('Coin') &&
      h.includes('Amount') &&
      h.some((x) => /^time\(utc/i.test(String(x).trim())) &&
      (h.includes('Transfer Network') ||
        h.includes('Withdrawal Address/Account') ||
        h.includes('Deposit Address')),
  },
];

function detectExchange(headers) {
  for (const sig of EXCHANGE_SIGNATURES) {
    if (sig.detect(headers)) return sig.name;
  }
  return 'Generic';
}

// ─────────────────────────────────────────────────────────────────────────────
// ABSCHNITT 5: Exchange-spezifische Parser
// Jeder Parser gibt ein Array von Roh-TX-Objekten zurück.
// Pro Zeile wird try/catch verwendet → kein Absturz bei defekten Zeilen.
// ─────────────────────────────────────────────────────────────────────────────

function parseBinance(rows, headers, failed, warnings) {
  // Binance hat zwei CSV-Formate:
  //   A) Transaction History: UTC_Time, Account, Operation, Coin, Change, Remark
  //   B) Trade History:       Date(UTC), Pair, Side, Price, Executed, Amount, Fee
  const isTradeHistory =
    headers.includes('Date(UTC)') && headers.includes('Pair') && headers.includes('Side');

  return isTradeHistory
    ? parseBinanceTrades(rows, headers, failed)
    : parseBinanceTxHistory(rows, headers, failed, warnings);
}

function parseBinanceTxHistory(rows, headers, failed, warnings) {
  const result = [];

  // Zeilen nach Zeitstempel (Minuten-Granularität) gruppieren.
  // Binance listet einen Trade als mehrere Zeilen (Sold/Revenue/Fee bzw. Spend/Buy/Fee).
  const groups = {};
  for (const row of rows) {
    const time = col(row, headers, 'UTC_Time', 'Time') || '';
    const key = time.slice(0, 16); // "YYYY-MM-DD HH:MM"
    if (!groups[key]) groups[key] = [];
    groups[key].push(row);
  }

  for (const group of Object.values(groups)) {
    processBinanceGroup(group, headers, result, failed, warnings);
  }

  return result;
}

/**
 * Verarbeitet eine Minutengruppe von Binance-Zeilen:
 *  - Pairing Sold↔Revenue, Spend↔Buy, Binance Convert (neg↔pos)
 *  - Small Assets Exchange BNB: Pairing per Remark "X to BNB"
 *  - Transaction Fee → separate gebuehr-TX (egal welcher Coin)
 *  - Rest (Deposit, Withdraw, Airdrop, ...) → einzeln über mapType()
 */
function processBinanceGroup(group, headers, result, failed, warnings) {
  const groupTradeStart = result.length; // erste Trade-TX dieser Sekundengruppe
  const items = group.map((row) => ({
    row,
    op: (col(row, headers, 'Operation') || '').toLowerCase(),
    change: parseAmount(col(row, headers, 'Change')),
    sym: normalizeAsset(col(row, headers, 'Coin')),
    remark: col(row, headers, 'Remark') || '',
    time: col(row, headers, 'UTC_Time', 'Time') || '',
  }));

  const { datum, zeit } = parseDateTime(items[0].time);
  if (!datum) {
    for (const it of items) {
      failed.push({ line: it.row._lineIndex, reason: 'Kein Datum', raw: it.op });
    }
    return;
  }

  const sold      = items.filter((i) => i.op === 'transaction sold');
  const revenue   = items.filter((i) => i.op === 'transaction revenue');
  const spend     = items.filter((i) => i.op === 'transaction spend');
  const buy       = items.filter((i) => i.op === 'transaction buy');
  const fee       = items.filter((i) => i.op === 'transaction fee');
  const convert   = items.filter((i) => i.op === 'binance convert');
  const smallBnb  = items.filter((i) => i.op === 'small assets exchange bnb');
  // Altes Binance-Format: "Transaction Related" — 2 Zeilen pro Trade (neg + pos)
  const related   = items.filter((i) => i.op === 'transaction related');

  const consumed = new Set([
    ...sold, ...revenue, ...spend, ...buy, ...fee,
    ...convert, ...smallBnb, ...related,
  ]);

  // ── SELL: Sold ↔ Revenue paaren (gleicher Index = gleicher Trade) ────────
  // Baut TX aus einem out+in-Paar: wenn eine Seite Fiat ist → verkauf/kauf mit
  // preis=Fiat-Menge (FIFO kann dann gv korrekt berechnen). Sonst Crypto→Crypto
  // bleibt typ='swap'. Behebt Bugs wo XLM→EUR oder EUR→XRP als preis=0-Swap
  // landete und Ergebnis/Anschaffungsbasis nicht ermittelbar war.
  const buildPairTx = (outSym, outAmt, inSym, inAmt, notiz) => {
    const outFiat = FEE_FIAT_SYMS.has(outSym);
    const inFiat  = FEE_FIAT_SYMS.has(inSym);
    if (inFiat && !outFiat) {
      return { typ: 'verkauf', sym: outSym, menge: outAmt, preis: inAmt,
               datum, zeit, gebuehr: 0, exchange: 'Binance', notiz };
    }
    if (outFiat && !inFiat) {
      return { typ: 'kauf', sym: inSym, menge: inAmt, preis: outAmt,
               datum, zeit, gebuehr: 0, exchange: 'Binance', notiz };
    }
    return { typ: 'swap', symOut: outSym, symIn: inSym,
             mengeOut: outAmt, mengeIn: inAmt,
             datum, zeit, preis: 0, gebuehr: 0, exchange: 'Binance', notiz };
  };

  const sellPairs = Math.min(sold.length, revenue.length);
  for (let i = 0; i < sellPairs; i++) {
    const s = sold[i];
    const r = revenue[i];
    if (!s.sym || !r.sym) continue;
    result.push(buildPairTx(s.sym, Math.abs(s.change), r.sym, Math.abs(r.change),
                            s.remark || `${s.sym} → ${r.sym}`));
  }
  for (let i = sellPairs; i < sold.length; i++) {
    const s = sold[i];
    if (!s.sym) continue;
    result.push({
      typ: 'verkauf', sym: s.sym, datum, zeit,
      menge: Math.abs(s.change), preis: 0, gebuehr: 0,
      exchange: 'Binance', notiz: s.remark,
    });
  }
  for (let i = sellPairs; i < revenue.length; i++) {
    const r = revenue[i];
    if (!r.sym) continue;
    result.push({
      typ: 'einzahlung', sym: r.sym, datum, zeit,
      menge: Math.abs(r.change), preis: 0, gebuehr: 0,
      exchange: 'Binance', notiz: r.remark || 'Transaction Revenue',
    });
  }

  // ── BUY: Spend ↔ Buy paaren ─────────────────────────────────────────────
  const buyPairs = Math.min(spend.length, buy.length);
  for (let i = 0; i < buyPairs; i++) {
    const sp = spend[i];
    const b = buy[i];
    if (!sp.sym || !b.sym) continue;
    result.push(buildPairTx(sp.sym, Math.abs(sp.change), b.sym, Math.abs(b.change),
                            sp.remark || `${sp.sym} → ${b.sym}`));
  }
  for (let i = buyPairs; i < spend.length; i++) {
    const sp = spend[i];
    if (!sp.sym) continue;
    result.push({
      typ: 'auszahlung', sym: sp.sym, datum, zeit,
      menge: Math.abs(sp.change), preis: 0, gebuehr: 0,
      exchange: 'Binance', notiz: sp.remark || 'Transaction Spend',
    });
  }
  for (let i = buyPairs; i < buy.length; i++) {
    const b = buy[i];
    if (!b.sym) continue;
    result.push({
      typ: 'kauf', sym: b.sym, datum, zeit,
      menge: Math.abs(b.change), preis: 0, gebuehr: 0,
      exchange: 'Binance', notiz: b.remark,
    });
  }

  // ── Binance Convert: neg ↔ pos paaren ───────────────────────────────────
  const convNeg = convert.filter((c) => c.change < 0);
  const convPos = convert.filter((c) => c.change > 0);
  const convPairs = Math.min(convNeg.length, convPos.length);
  for (let i = 0; i < convPairs; i++) {
    const n = convNeg[i];
    const p = convPos[i];
    if (!n.sym || !p.sym) continue;
    result.push(buildPairTx(n.sym, Math.abs(n.change), p.sym, Math.abs(p.change),
                            'Binance Convert'));
  }

  // ── Transaction Related (altes Format): neg ↔ pos paaren ────────────────
  const relNeg = related.filter((r) => r.change < 0);
  const relPos = related.filter((r) => r.change > 0);
  const relPairs = Math.min(relNeg.length, relPos.length);
  for (let i = 0; i < relPairs; i++) {
    const n = relNeg[i];
    const p = relPos[i];
    if (!n.sym || !p.sym) continue;
    result.push(buildPairTx(n.sym, Math.abs(n.change), p.sym, Math.abs(p.change),
                            `${n.sym} → ${p.sym}`));
  }
  for (let i = relPairs; i < relNeg.length; i++) {
    const n = relNeg[i];
    if (!n.sym) continue;
    result.push({
      typ: 'auszahlung', sym: n.sym, datum, zeit,
      menge: Math.abs(n.change), preis: 0, gebuehr: 0,
      exchange: 'Binance', notiz: 'Transaction Related',
    });
  }
  for (let i = relPairs; i < relPos.length; i++) {
    const p = relPos[i];
    if (!p.sym) continue;
    result.push({
      typ: 'einzahlung', sym: p.sym, datum, zeit,
      menge: Math.abs(p.change), preis: 0, gebuehr: 0,
      exchange: 'Binance', notiz: 'Transaction Related',
    });
  }

  // ── Small Assets Exchange BNB: paaren per Remark "X to BNB" ─────────────
  if (smallBnb.length > 0) {
    const bySource = {};
    for (const it of smallBnb) {
      const m = (it.remark || '').match(/^(\S+)\s+to\s+BNB/i);
      const key = m ? normalizeAsset(m[1]) : it.sym;
      if (!bySource[key]) bySource[key] = [];
      bySource[key].push(it);
    }
    for (const list of Object.values(bySource)) {
      // Negative Seite = der konvertierte Coin (==source), positive Seite = BNB
      const outItem = list.find((it) => it.change < 0);
      const inItem  = list.find((it) => it.sym === 'BNB' && it.change > 0);
      if (outItem && inItem && outItem.sym && outItem.sym !== 'BNB') {
        result.push({
          typ: 'swap',
          symOut: outItem.sym,
          symIn: 'BNB',
          mengeOut: Math.abs(outItem.change),
          mengeIn: Math.abs(inItem.change),
          datum, zeit, preis: 0, gebuehr: 0,
          exchange: 'Binance',
          notiz: `${outItem.sym} → BNB (Small Assets)`,
        });
      } else {
        // Sonderfall: BNB-Seite konnte nicht eindeutig zugeordnet werden → roh emittieren
        for (const it of list) {
          if (!it.sym || it.change === 0) continue;
          result.push({
            typ: it.change > 0 ? 'einzahlung' : 'auszahlung',
            sym: it.sym, datum, zeit,
            menge: Math.abs(it.change), preis: 0, gebuehr: 0,
            exchange: 'Binance',
            notiz: 'Small Assets Exchange BNB',
          });
        }
      }
    }
  }

  // ── Transaction Fee: erst versuchen in den passenden Trade einzuarbeiten,
  // sonst als separate gebuehr-TX emittieren (z.B. BNB-Discount-Fee bei nicht-
  // BNB-Trade — die muss das BNB-Lot verbrauchen).
  // groupTradeStart markiert den ersten in dieser Gruppe gepushten Trade,
  // so dass wir nur INNERHALB der aktuellen Sekundengruppe matchen.
  const groupTrades = result.slice(groupTradeStart);
  for (const f of fee) {
    if (!f.sym || f.change === 0) continue;
    const feeCoin = f.sym;
    const feeAmt  = Math.abs(f.change);
    const target  = groupTrades.find(tx => {
      const buy  = tx.typ === 'swap' ? tx.symIn  : (tx.typ === 'kauf'    ? tx.sym : null);
      const sell = tx.typ === 'swap' ? tx.symOut : (tx.typ === 'verkauf' ? tx.sym : null);
      return buy === feeCoin || sell === feeCoin;
    });
    const absorbed = target ? applyTradingFee(target, feeCoin, feeAmt) : false;
    if (!absorbed) {
      // Tagge die zugehoerige Trade-TX (falls vorhanden), damit das Edit-Form
      // dem User zeigen kann "Gebuehr existiert als separate TX".
      if (target && !target._fee) {
        target._fee = { amount: feeAmt, coin: feeCoin, absorbed: 'separate_tx' };
      }
      result.push({
        typ: 'gebuehr',
        sym: f.sym,
        datum, zeit,
        menge: feeAmt,
        preis: 0, gebuehr: 0,
        exchange: 'Binance',
        notiz: 'Trading Fee',
      });
    }
  }

  // ── Rest (Deposit, Withdraw, Airdrop, Distribution, Asset Recovery, ...) ─
  for (const it of items) {
    if (consumed.has(it)) continue;
    try {
      if (it.change === 0) continue;
      if (!it.sym) {
        failed.push({ line: it.row._lineIndex, reason: 'Kein Symbol', raw: it.op });
        continue;
      }

      // NFT-Lots (Airdrop Assets, Asset Recovery) sind keine Vermögenswerte im
      // fungiblen Sinn → weiterhin ausblenden (bleiben über den Sonstige-Tab
      // einsehbar, falls vorhanden).
      if (it.sym === 'NFT') continue;

      // Fiat-Ein-/Auszahlungen (EUR Deposit/Withdraw) sind kein Krypto-Bestands-
      // ereignis (downstream über coin∈FIAT ausgeschlossen), aber ein realer
      // Geldfluss. NICHT mehr verwerfen — als eigene Kategorie führen. Richtung
      // aus dem Vorzeichen von `it.change`.
      if (FEE_FIAT_SYMS.has(it.sym)) {
        result.push(buildFiatTx(it.sym, it.change, {
          datum, zeit, exchange: 'Binance', notiz: it.remark,
        }));
        continue;
      }

      let typ = mapType(it.op);
      // Token Redenomination/Rebranding und Asset Recovery sind interne
      // Chain-Events — keine Gegenseite erforderlich, daher als externer
      // Transfer markieren damit sie nicht als "offener Transfer" auftauchen.
      let externalTransfer = false;

      // Binance „Withdraw" und „Deposit" sind per Definition externe Bewegungen
      // (Coins verlassen/erreichen die Börse via Blockchain). Wenn der User
      // die Gegenseite NICHT importiert hat (z. B. Cold Wallet, fremde Börse),
      // soll FIFO die Lots trotzdem verbrauchen statt sie als Phantom-Bestand
      // hängen zu lassen. Der transferMatcher pairt sie weiterhin, falls eine
      // passende Einzahlung/Auszahlung auf einer anderen Börse existiert —
      // matchedOutIds gewinnt vor externalTransfer in fifo.js.
      const opLower = it.op.toLowerCase();
      if (opLower === 'withdraw' || opLower === 'deposit') {
        externalTransfer = true;
      }

      // Sign-basierter Fallback für richtungsabhängige Ops
      if (typ === 'unknown') {
        if (it.op === 'asset recovery' || it.op === 'token swap - redenomination/rebranding') {
          typ = it.change > 0 ? 'einzahlung' : 'auszahlung';
          externalTransfer = true;
        } else {
          warnings.push(`Binance: Unbekannter Operationstyp "${it.op}" (Zeile ${it.row._lineIndex})`);
          continue;
        }
      }

      result.push({
        typ,
        sym: it.sym,
        datum, zeit,
        menge: Math.abs(it.change),
        preis: 0, gebuehr: 0,
        exchange: 'Binance',
        notiz: it.remark,
        ...(externalTransfer ? { externalTransfer: true } : {}),
      });
    } catch (err) {
      failed.push({ line: it.row._lineIndex, reason: err.message, raw: JSON.stringify(it.row).slice(0, 80) });
    }
  }
}

function parseBinanceTrades(rows, headers, failed) {
  const result = [];

  for (const row of rows) {
    try {
      const pairRaw = col(row, headers, 'Pair') || '';
      const side = (col(row, headers, 'Side') || '').toLowerCase();
      const execRaw = col(row, headers, 'Executed') || '';   // z.B. "0.5BTC"
      const amountRaw = col(row, headers, 'Amount') || '';   // z.B. "22000USDT"
      const feeRaw = col(row, headers, 'Fee') || '';         // z.B. "0.001BNB"
      const { datum, zeit } = parseDateTime(col(row, headers, 'Date(UTC)', 'Date'));

      if (!datum || !pairRaw) continue;

      // Parse "0.5BTC" → { amount: 0.5, sym: 'BTC' }
      const parseSym = (s) => {
        const m = String(s).match(/^([0-9.,]+)\s*([A-Z]+)$/);
        if (!m) return null;
        return { amount: parseAmount(m[1]), sym: normalizeAsset(m[2]) };
      };

      const exec = parseSym(execRaw);
      const amount = parseSym(amountRaw);
      const fee = parseSym(feeRaw);

      if (!exec || !amount) {
        failed.push({ line: row._lineIndex, reason: `Konnte Executed/Amount nicht parsen: "${execRaw}" / "${amountRaw}"`, raw: pairRaw });
        continue;
      }

      let tx = null;
      if (side === 'buy') {
        tx = {
          typ: 'swap',
          symIn: exec.sym,    mengeIn: exec.amount,
          symOut: amount.sym, mengeOut: amount.amount,
          datum, zeit, preis: 0, gebuehr: 0,
          exchange: 'Binance',
        };
      } else if (side === 'sell') {
        tx = {
          typ: 'swap',
          symOut: exec.sym,   mengeOut: exec.amount,
          symIn: amount.sym,  mengeIn: amount.amount,
          datum, zeit, preis: 0, gebuehr: 0,
          exchange: 'Binance',
        };
      }
      if (!tx) continue;

      // Fee in der passenden Coin → in den Trade einarbeiten; Fee in dritter
      // Coin (BNB-Discount auf nicht-BNB-Trade) → separate gebuehr-TX.
      const absorbed = fee ? applyTradingFee(tx, fee.sym, fee.amount) : false;
      result.push(tx);
      if (fee && !absorbed && fee.amount > 0) {
        result.push({
          typ: 'gebuehr',
          sym: fee.sym,
          datum, zeit,
          menge: fee.amount,
          preis: 0, gebuehr: 0,
          exchange: 'Binance',
          notiz: 'Trading Fee',
        });
      }
    } catch (err) {
      failed.push({ line: row._lineIndex, reason: err.message, raw: JSON.stringify(row).slice(0, 80) });
    }
  }

  return result;
}

function parseKraken(rows, headers, failed, warnings) {
  const result = [];

  for (const row of rows) {
    try {
      const rawType = (col(row, headers, 'type') || '').toLowerCase();
      const asset = normalizeAsset(col(row, headers, 'asset') || '');
      const amount = parseAmount(col(row, headers, 'amount'));
      const fee = parseAmount(col(row, headers, 'fee'));
      const { datum, zeit } = parseDateTime(col(row, headers, 'time'));
      const hash = col(row, headers, 'txid') || '';

      if (!datum) { failed.push({ line: row._lineIndex, reason: 'Kein Datum', raw: rawType }); continue; }
      if (!asset) { failed.push({ line: row._lineIndex, reason: 'Kein Asset', raw: rawType }); continue; }
      if (Math.abs(amount) === 0) continue;

      // Kraken "transfer" und "withdrawal" am Vorzeichen unterscheiden
      let typ;
      if (rawType === 'deposit') {
        typ = 'einzahlung';
      } else if (rawType === 'withdrawal' || rawType === 'transfer') {
        typ = amount >= 0 ? 'einzahlung' : 'auszahlung';
      } else {
        typ = mapType(rawType);
      }

      // Sicherheitsnetz: unbekannter Typ, aber Asset ist Fiat → als Fiat-Ein-/
      // Auszahlung führen statt verwerfen (Richtung aus Vorzeichen).
      if (typ === 'unknown' && FEE_FIAT_SYMS.has(asset)) {
        result.push(buildFiatTx(asset, amount, {
          datum, zeit, exchange: 'Kraken', notiz: rawType, eurValue: asset === 'EUR' ? Math.abs(amount) : 0,
        }));
        continue;
      }

      if (typ === 'unknown') {
        warnings.push(`Kraken: Unbekannter Typ "${rawType}" (Zeile ${row._lineIndex})`);
        continue;
      }

      // Fiat-Ein-/Auszahlung (Kraken bucht EUR-Deposits als type=deposit, asset=EUR)
      // → eigene Kategorie mit EUR-Anzeigewert, bestandsneutral fürs Krypto-Portfolio.
      if ((typ === 'einzahlung' || typ === 'auszahlung') && FEE_FIAT_SYMS.has(asset)) {
        result.push(buildFiatTx(asset, amount, {
          datum, zeit, exchange: 'Kraken', notiz: rawType, eurValue: asset === 'EUR' ? Math.abs(amount) : 0, typ,
        }));
        continue;
      }

      result.push({
        typ,
        sym: asset,
        datum,
        zeit,
        menge: Math.abs(amount),
        preis: 0,
        gebuehr: Math.abs(fee),
        exchange: 'Kraken',
        hash,
      });
    } catch (err) {
      failed.push({ line: row._lineIndex, reason: err.message, raw: JSON.stringify(row).slice(0, 80) });
    }
  }

  return result;
}

function parseCoinbase(rows, headers, failed, warnings) {
  const result = [];

  for (const row of rows) {
    try {
      const rawType = col(row, headers, 'Transaction Type', 'Type') || '';
      const asset = normalizeAsset(col(row, headers, 'Asset') || '');
      const qty = parseAmount(col(row, headers, 'Quantity Transacted', 'Quantity'));
      // Coinbase exportiert EUR-Werte in Spalten mit "EUR" im Namen
      const spotEur = parseAmount(
        col(row, headers, 'Spot Price at Transaction', 'EUR Price At Transaction', 'Spot Price (EUR)', 'Price at Transaction') || '0'
      );
      const total = parseAmount(
        col(row, headers, 'Total (inclusive of fees and/or spread)', 'EUR Total (inclusive of fees)', 'Total') || '0'
      );
      const fees = parseAmount(
        col(row, headers, 'Fees and/or Spread', 'EUR Fees and/or Spread', 'Fees') || '0'
      );
      const { datum, zeit } = parseDateTime(col(row, headers, 'Timestamp'));
      const notiz = col(row, headers, 'Notes') || '';

      if (!datum) { failed.push({ line: row._lineIndex, reason: 'Kein Datum', raw: rawType }); continue; }
      if (!asset) { failed.push({ line: row._lineIndex, reason: 'Kein Asset', raw: rawType }); continue; }
      if (qty === 0) continue;

      const typ = mapType(rawType);
      // Sicherheitsnetz: unbekannter Typ, aber Asset ist Fiat → Fiat-Ein-/
      // Auszahlung führen statt verwerfen. Richtung aus Total-Vorzeichen
      // (Coinbase bucht ausgehende Beträge negativ).
      if (typ === 'unknown' && FEE_FIAT_SYMS.has(asset)) {
        const rawTotal = parseAmount(
          col(row, headers, 'Total (inclusive of fees and/or spread)', 'EUR Total (inclusive of fees)', 'Total') || '0'
        );
        const dir = rawTotal < 0 ? -Math.abs(qty) : Math.abs(qty);
        result.push(buildFiatTx(asset, dir, {
          datum, zeit, exchange: 'Coinbase', notiz: rawType, eurValue: Math.abs(rawTotal),
        }));
        continue;
      }
      if (typ === 'unknown') {
        warnings.push(`Coinbase: Unbekannter Typ "${rawType}" (Zeile ${row._lineIndex})`);
        continue;
      }

      // EUR-Gesamtwert: aus Total-Spalte (auch negativ = ausgehend) oder Spot*Qty.
      // Math.abs(total) statt total>0, damit ausgehende TXs (Send: -€92,38) auch
      // ihren EUR-Wert bekommen statt 0.
      const preis = Math.abs(total) > 0
        ? Math.abs(total) - Math.abs(fees)
        : Math.abs(spotEur * qty);

      // ── Convert (Coinbase "Convert X to Y") ──────────────────────────────────
      // mapType('convert') = 'swap', aber die Zeile hat nur EINEN Asset (die
      // Abgabe-Seite). Die Empfangs-Seite steht NUR im Notes-Feld:
      //   "Converted 112.360736 USDC to 9642.15954382 FLR"
      // Ohne Parsing entsteht ein einseitiger Swap (coinOut/coinIn=null) → die
      // App crasht beim Rendern (schwarzer Bildschirm) und FIFO kann ihn nicht
      // bewerten. Wir zerlegen Notes in beide Seiten.
      if (typ === 'swap') {
        const m = notiz.match(/Converted\s+([\d.,]+)\s+([A-Za-z0-9]+)\s+to\s+([\d.,]+)\s+([A-Za-z0-9]+)/i);
        if (m) {
          const outAmt = parseAmount(m[1]);
          const outSym = normalizeAsset(m[2]);
          const inAmt  = parseAmount(m[3]);
          const inSym  = normalizeAsset(m[4]);
          // EUR-Wert des Tauschs (für FIFO-Kostenbasis). Total ist inkl. Fees,
          // Subtotal ohne — wir nehmen den Brutto-EUR-Wert als preis.
          const eurVal = Math.abs(total) > 0 ? Math.abs(total) : Math.abs(preis);
          // Eine Fiat-Seite (EUR/USD…) → der canonicalNormalizer wandelt es
          // später zu kauf/verkauf. Stablecoins (USDC) bleiben Crypto-Swap.
          result.push({
            typ: 'swap',
            symOut: outSym, mengeOut: outAmt,
            symIn:  inSym,  mengeIn:  inAmt,
            datum, zeit,
            preis: eurVal,
            gebuehr: Math.abs(fees),
            exchange: 'Coinbase',
            notiz,
          });
          continue;
        }
        // Notes nicht parsebar → als Fehler markieren statt kaputten Swap zu bauen
        failed.push({ line: row._lineIndex, reason: 'Coinbase Convert: Notes nicht parsebar', raw: notiz.slice(0, 60) });
        continue;
      }

      // ── Send / Receive: Wallet-Transfers (externe Adressen) ──────────────────
      // Coinbase "Send"/"Receive" gehen an/von externe(n) Blockchain-Adressen.
      // Wie bei Binance Withdraw/Deposit als externalTransfer flaggen, damit FIFO
      // die Lots konsumiert wenn keine Gegenseite importiert ist (sonst Phantom-
      // Bestand). Bei importierter Gegenseite (Ledger) gewinnt das Transfer-Match.
      const rawLower = rawType.toLowerCase();
      const isExternalTransfer = (rawLower === 'send' || rawLower === 'receive')
        && (typ === 'auszahlung' || typ === 'einzahlung');

      result.push({
        typ,
        sym: asset,
        datum,
        zeit,
        menge: Math.abs(qty),
        preis: Math.abs(preis),
        gebuehr: Math.abs(fees),
        stueck: qty > 0 && preis > 0 ? Math.abs(preis) / Math.abs(qty) : 0,
        exchange: 'Coinbase',
        notiz,
        ...(isExternalTransfer ? { externalTransfer: true } : {}),
      });
    } catch (err) {
      failed.push({ line: row._lineIndex, reason: err.message, raw: JSON.stringify(row).slice(0, 80) });
    }
  }

  return result;
}

/**
 * Bitget Spot Account Statement Parser.
 *
 * Format: order, Date, Coin, Type, Amount, Fee, Available
 * Operations:
 *   - Deposit / Withdraw → einzahlung / auszahlung
 *   - Sell [coin] + Buy [USDT] at same timestamp → swap
 *   - Transfer out / Transfer in → SKIP (Spot↔Futures internal, kein eigener Lot-Effekt:
 *       der USDT-Lot bleibt in der globalen FIFO und wird von Futures-PnL-Verlusten konsumiert)
 */
function parseBitgetSpot(rows, headers, failed) {
  const result = [];

  // Gruppenkey = vollständiger Zeitstempel (sekunden-genau) für Trade-Pairing
  const groups = {};
  for (const row of rows) {
    const time = col(row, headers, 'Date') || '';
    if (!groups[time]) groups[time] = [];
    groups[time].push(row);
  }

  const SKIP_TYPES = new Set(['transfer out', 'transfer in']);

  for (const [timeKey, group] of Object.entries(groups)) {
    const { datum, zeit } = parseDateTime(timeKey);
    if (!datum) {
      for (const row of group) failed.push({ line: row._lineIndex, reason: 'Kein Datum', raw: timeKey });
      continue;
    }

    // Sells (Coin raus) und Buys (Coin rein) für Swap-Pairing sammeln
    const sellsByCoin = {}; // coin → { amount, fee }
    const buysByCoin  = {}; // coin → { amount, fee }

    for (const row of group) {
      try {
        const type   = (col(row, headers, 'Type')   || '').toLowerCase().trim();
        const sym    = normalizeAsset(col(row, headers, 'Coin') || '');
        const amount = parseAmount(col(row, headers, 'Amount') || '0');
        const fee    = Math.abs(parseAmount(col(row, headers, 'Fee') || '0'));

        if (!sym || amount === 0) continue;
        if (SKIP_TYPES.has(type)) continue; // interne Spot↔Futures Umbuchung ignorieren

        if (type === 'sell') {
          if (!sellsByCoin[sym]) sellsByCoin[sym] = { amount: 0, fee: 0 };
          sellsByCoin[sym].amount += Math.abs(amount);
          sellsByCoin[sym].fee   += fee;
        } else if (type === 'buy') {
          if (!buysByCoin[sym]) buysByCoin[sym] = { amount: 0, fee: 0 };
          buysByCoin[sym].amount += Math.abs(amount);
          buysByCoin[sym].fee    += fee;
        } else if (type === 'deposit') {
          result.push({
            typ: 'einzahlung', sym, datum, zeit,
            menge: Math.abs(amount), preis: 0, gebuehr: fee,
            exchange: 'Bitget',
          });
        } else if (type === 'withdraw' || type === 'withdrawal') {
          result.push({
            typ: 'auszahlung', sym, datum, zeit,
            menge: Math.abs(amount), preis: 0, gebuehr: fee,
            exchange: 'Bitget',
          });
        }
        // Alle anderen Typen still überspringen
      } catch (err) {
        failed.push({ line: row._lineIndex, reason: err.message, raw: JSON.stringify(row).slice(0, 80) });
      }
    }

    // Sell- und Buy-Legs als Swaps paaren
    const sellCoins = Object.keys(sellsByCoin);
    const buyCoins  = Object.keys(buysByCoin);

    if (sellCoins.length === 1 && buyCoins.length === 1) {
      // Einfaches Pair: 1 Coin out → 1 Coin in
      const outCoin = sellCoins[0];
      const inCoin  = buyCoins[0];
      result.push({
        typ:      'swap',
        symOut:   outCoin, mengeOut: sellsByCoin[outCoin].amount,
        symIn:    inCoin,  mengeIn:  buysByCoin[inCoin].amount,
        datum, zeit, preis: 0,
        gebuehr:  sellsByCoin[outCoin].fee + buysByCoin[inCoin].fee,
        exchange: 'Bitget',
      });
    } else if (sellCoins.length > 0 || buyCoins.length > 0) {
      // Mehrdeutiger Fall: getrennte auszahlung/einzahlung emittieren
      for (const [sym, d] of Object.entries(sellsByCoin)) {
        result.push({
          typ: 'auszahlung', sym, datum, zeit,
          menge: d.amount, preis: 0, gebuehr: d.fee,
          exchange: 'Bitget',
        });
      }
      for (const [sym, d] of Object.entries(buysByCoin)) {
        result.push({
          typ: 'einzahlung', sym, datum, zeit,
          menge: d.amount, preis: 0, gebuehr: d.fee,
          exchange: 'Bitget',
        });
      }
    }
  }

  return result;
}

function parseBitget(rows, headers, failed) {
  const result = [];
  const KNOWN_QUOTES = ['USDT', 'USDC', 'BTC', 'ETH', 'EUR', 'BNB', 'BUSD'];

  for (const row of rows) {
    try {
      const side = (col(row, headers, 'side', 'Side') || '').toLowerCase();
      const symRaw = col(row, headers, 'symbol', 'Symbol') || '';
      const filled = parseAmount(col(row, headers, 'filled', 'Filled', 'execQty'));
      const filledAmt = parseAmount(col(row, headers, 'filledAmount', 'FilledAmount', 'quoteVolume', 'quoteSize'));
      const fee = parseAmount(col(row, headers, 'fee', 'Fee'));
      const feeCoinRaw = col(row, headers, 'feeCoin', 'FeeCoin', 'feeCurrency', 'Fee Currency') || '';
      const { datum, zeit } = parseDateTime(col(row, headers, 'createTime', 'Created Time', 'time', 'Time'));
      const status = (col(row, headers, 'status', 'Status') || '').toLowerCase();

      if (!datum) continue;
      // Nur abgeschlossene Orders
      if (status && !['full_fill', 'filled', 'done', 'fully_fill'].includes(status)) continue;
      if (!side || !symRaw) continue;
      if (filled === 0) continue;

      // "BTCUSDT_SPBL" → base=BTC, quote=USDT
      const cleanSym = symRaw.replace(/_SPBL|_UMCBL/g, '');
      let base = cleanSym, quote = '';
      for (const q of KNOWN_QUOTES) {
        if (cleanSym.endsWith(q)) {
          base = cleanSym.slice(0, -q.length);
          quote = q;
          break;
        }
      }

      if (!base || !quote) {
        failed.push({ line: row._lineIndex, reason: `Symbol nicht parsebar: "${symRaw}"`, raw: symRaw });
        continue;
      }

      const isBuy = side === 'buy';
      const tx = {
        typ: 'swap',
        symOut: normalizeAsset(isBuy ? quote : base),
        symIn:  normalizeAsset(isBuy ? base  : quote),
        mengeOut: isBuy ? filledAmt : filled,
        mengeIn:  isBuy ? filled    : filledAmt,
        datum, zeit,
        preis: 0,
        gebuehr: 0,
        exchange: 'Bitget',
      };
      // Fee korrekt zuordnen — wenn Fee-Coin bekannt: in passende Trade-Seite
      // einarbeiten; sonst Fallback auf gebuehr-Feld wie bisher.
      const absorbed = feeCoinRaw
        ? applyTradingFee(tx, feeCoinRaw, Math.abs(fee))
        : (tx.gebuehr = Math.abs(fee), true);
      result.push(tx);
      if (feeCoinRaw && !absorbed && Math.abs(fee) > 0) {
        result.push({
          typ: 'gebuehr', sym: normalizeAsset(feeCoinRaw),
          datum, zeit, menge: Math.abs(fee),
          preis: 0, gebuehr: 0, exchange: 'Bitget', notiz: 'Trading Fee',
        });
      }
    } catch (err) {
      failed.push({ line: row._lineIndex, reason: err.message, raw: JSON.stringify(row).slice(0, 80) });
    }
  }

  return result;
}

function parseBitgetFutures(rows, headers, failed) {
  const result = [];

  // Types that produce no transaction:
  // - open_long/open_short: position entry, amount=0, fee captured in close
  // - trans_from_exchange/trans_to_exchange: INTERNAL same-account transfer (Spot→Futures).
  //   The USDT lots already exist in the pool from Spot purchases. Creating a new lot here
  //   would DOUBLE the balance. Futures losses (close_*) will consume the Spot lots directly
  //   via the wallet-fallback in fifoMatch. Skipping these is the correct accounting.
  // - risk_captital_user_transfer: tiny internal risk capital adjustment, irrelevant
  // open_long/open_short werden weiter oben behandelt (Fee-Buchung, sonst skip).
  const SKIP_TYPES = new Set([
    'trans_from_exchange', 'trans_to_exchange',
    'risk_captital_user_transfer', 'risk_capital_user_transfer',
  ]);

  // Fee-Verbrauch: pro Zeile mit Fee>0 (in USDT) eine 'gebuehr'-TX emittieren,
  // damit fifoMatch den USDT-Pool tatsaechlich dekrementiert. Sonst sammeln sich
  // hunderte open_*/close_*-Fees, die NIE aus dem Bestand abgehen --> Phantom-USDT.
  const emitFee = (sym, fee, datum, zeit, srcType) => {
    if (!sym || !(fee > 0)) return;
    result.push({
      typ: 'gebuehr',
      sym, coin: sym, menge: fee,
      datum, zeit,
      preis: 0, gebuehr: 0,
      exchange: 'Bitget Futures',
      notiz: `Futures-Fee (${srcType})`,
    });
  };

  for (const row of rows) {
    try {
      const type   = (col(row, headers, 'Type')   || '').toLowerCase().trim();
      const sym    = normalizeAsset(col(row, headers, 'Coin') || '');
      const amount = parseAmount(col(row, headers, 'Amount') || '0'); // signed
      const fee    = Math.abs(parseAmount(col(row, headers, 'Fee') || '0'));
      const { datum, zeit } = parseDateTime(col(row, headers, 'Date') || '');
      // Futures-Pair (BTCUSDT, XRPUSDT, ETHUSDT, ...) fuer Anzeige in der Karte
      const pair = (col(row, headers, 'Futures') || '').trim();
      const futuresPair = pair && pair.toUpperCase() !== 'NULL' ? pair : null;

      if (!datum) continue;
      if (!sym)   continue;

      // open_long/open_short tragen Amount=0 aber haben Fees -- die muessen wir
      // trotzdem buchen (sonst bleibt USDT im Pool, das real abgegangen ist).
      if (type === 'open_long' || type === 'open_short') {
        emitFee(sym, fee, datum, zeit, type);
        continue;
      }

      if (SKIP_TYPES.has(type)) continue;

      if (['close_long', 'close_short', 'burst_close_long', 'burst_close_short'].includes(type)) {
        // Position close / liquidation → futures_pnl
        // amount is signed: positive = gain, negative = loss
        if (Math.abs(amount) < 1e-9 && fee < 1e-9) continue;
        if (Math.abs(amount) > 1e-9) {
          // Position-Side ablesen (long/short, burst=Liquidation) — fuer UI
          const isLiquidation = type.startsWith('burst_');
          const positionSide = type.includes('long') ? 'long' : 'short';
          const entry = {
            typ: 'futures_pnl',
            sym, datum, zeit,
            menge: Math.abs(amount),
            preis: amount, // SIGNED — toCanonical preserves this for futures_pnl
            gebuehr: 0,    // Fee wird separat als gebuehr-TX emittiert (siehe unten)
            exchange: 'Bitget Futures',
            ...(futuresPair ? { futuresPair } : {}),
            positionSide,
            ...(isLiquidation ? { liquidation: true } : {}),
          };
          result.push(entry);
        }
        // Fee zusaetzlich als gebuehr buchen (USDT-Pool-Verbrauch)
        emitFee(sym, fee, datum, zeit, type);

      } else if (['contract_margin_settle_fee', 'contract_main_settle_fee'].includes(type)) {
        // Funding / settlement fee — amount signed
        if (Math.abs(amount) < 1e-9) continue;
        result.push({
          typ: 'funding_fee',
          sym, datum, zeit,
          menge: Math.abs(amount),
          preis: amount, // SIGNED
          gebuehr: 0,
          exchange: 'Bitget Futures',
        });
      }
      // All remaining types silently skipped
    } catch (err) {
      failed.push({ line: row._lineIndex, reason: err.message, raw: JSON.stringify(row).slice(0, 80) });
    }
  }

  return result;
}

// Bitvavo "Vollständige Transaktionshistorie":
// Timezone,Date,Time,Type,Currency,Amount,Quote Currency,Quote Price,
// Received / Paid Currency,Received / Paid Amount,Fee currency,Fee amount,Status,...
//   buy:        Currency=Coin, Amount=+Menge, Received/Paid Amount=-EUR (gezahlt, inkl. Fee)
//   sell:       Currency=Coin, Amount=-Menge, Received/Paid Amount=+EUR (erhalten, netto Fee)
//   deposit/withdrawal: Currency=EUR (Fiat) → wird downstream gefiltert
// preis = |Received/Paid Amount| ist der reale EUR-Cashflow (Fee bereits darin
// reflektiert) → gebuehr=0, damit FIFO (kauf: preis+gebuehr / verkauf: preis-gebuehr)
// die Fee nicht doppelt zählt.
function parseBitvavoFullHistory(rows, headers, failed, warnings) {
  const result = [];
  for (const row of rows) {
    try {
      const typRaw = (col(row, headers, 'Type') || '').toLowerCase().trim();
      const sym    = normalizeAsset(col(row, headers, 'Currency') || '');
      const amount = parseAmount(col(row, headers, 'Amount') || '0');
      const eurAmt = parseAmount(col(row, headers, 'Received / Paid Amount') || '0');
      const status = (col(row, headers, 'Status') || '').toLowerCase().trim();
      const d = col(row, headers, 'Date') || '';
      const t = col(row, headers, 'Time') || '';
      const { datum, zeit } = parseDateTime(`${d} ${t}`.trim());

      if (status && status !== 'completed') continue;
      if (!datum || !sym || amount === 0) continue;

      if (typRaw === 'buy' || typRaw === 'sell') {
        result.push({
          typ:   typRaw === 'buy' ? 'kauf' : 'verkauf',
          sym,
          datum, zeit,
          menge: Math.abs(amount),
          preis: Math.abs(eurAmt),  // realer EUR-Cashflow, Fee bereits enthalten
          gebuehr: 0,
          stueck: amount !== 0 && eurAmt !== 0 ? Math.abs(eurAmt) / Math.abs(amount) : 0,
          exchange: 'Bitvavo',
          hash: col(row, headers, 'Transaction ID') || '',
        });
      } else if (typRaw === 'deposit' || typRaw === 'withdrawal') {
        // Meist Fiat (EUR) → downstream gefiltert. Crypto-Transfers würden via
        // transferMatcher gegen andere Börsen matchen.
        result.push({
          typ: typRaw === 'deposit' ? 'einzahlung' : 'auszahlung',
          sym, datum, zeit,
          menge: Math.abs(amount),
          preis: 0, gebuehr: 0,
          exchange: 'Bitvavo',
          hash: col(row, headers, 'Transaction ID') || '',
        });
      } else if (typRaw === 'staking' || typRaw === 'staking reward' || typRaw === 'reward') {
        result.push({
          typ: 'staking', sym, datum, zeit,
          menge: Math.abs(amount), preis: 0, gebuehr: 0, exchange: 'Bitvavo',
        });
      } else {
        warnings.push(`Bitvavo: Unbekannter Typ "${typRaw}" (Zeile ${row._lineIndex})`);
      }
    } catch (err) {
      failed.push({ line: row._lineIndex, reason: err.message, raw: JSON.stringify(row).slice(0, 80) });
    }
  }
  return result;
}

function parseBitvavo(rows, headers, failed, warnings) {
  // Format-Weiche: "Vollständige Transaktionshistorie" (kein Market, dafür
  // Currency + Quote Price + Received/Paid) vs. klassischer Trade-Export (Market).
  if (!headers.includes('Market') && headers.includes('Received / Paid Amount')) {
    return parseBitvavoFullHistory(rows, headers, failed, warnings);
  }
  const result = [];

  for (const row of rows) {
    try {
      const market = col(row, headers, 'Market') || '';
      const side = (col(row, headers, 'Side') || '').toLowerCase();
      const typRaw = (col(row, headers, 'Type') || '').toLowerCase();
      const amount = parseAmount(col(row, headers, 'Amount'));
      const price = parseAmount(col(row, headers, 'Price'));
      const fee = parseAmount(col(row, headers, 'Fee amount', 'Fee Amount'));
      const feeCoinRaw = col(row, headers, 'Fee currency', 'Fee Currency', 'Fee asset', 'Fee Asset') || '';
      const { datum, zeit } = parseDateTime(col(row, headers, 'Date', 'Time'));

      if (!datum || !market) continue;

      const [base, quote] = market.split('-');

      if (side === 'buy' || side === 'sell') {
        const isBuy = side === 'buy';
        const eurVal = amount * price;
        const tx = {
          typ: 'swap',
          symOut: normalizeAsset(isBuy ? quote : base),
          symIn:  normalizeAsset(isBuy ? base  : quote),
          mengeOut: isBuy ? eurVal : amount,
          mengeIn:  isBuy ? amount : eurVal,
          datum, zeit,
          preis: quote === 'EUR' ? eurVal : 0,
          gebuehr: 0,
          exchange: 'Bitvavo',
        };
        const absorbed = feeCoinRaw
          ? applyTradingFee(tx, feeCoinRaw, Math.abs(fee))
          : (tx.gebuehr = Math.abs(fee), true);
        result.push(tx);
        if (feeCoinRaw && !absorbed && Math.abs(fee) > 0) {
          result.push({
            typ: 'gebuehr', sym: normalizeAsset(feeCoinRaw),
            datum, zeit, menge: Math.abs(fee),
            preis: 0, gebuehr: 0, exchange: 'Bitvavo', notiz: 'Trading Fee',
          });
        }
      } else if (typRaw === 'deposit' || typRaw === 'withdrawal') {
        result.push({
          typ: typRaw === 'deposit' ? 'einzahlung' : 'auszahlung',
          sym: normalizeAsset(base || market),
          datum, zeit,
          menge: Math.abs(amount),
          preis: 0,
          gebuehr: Math.abs(fee),
          exchange: 'Bitvavo',
        });
      } else if (typRaw === 'staking' || typRaw === 'staking reward') {
        result.push({
          typ: 'staking',
          sym: normalizeAsset(base || market),
          datum, zeit,
          menge: Math.abs(amount),
          preis: 0,
          gebuehr: 0,
          exchange: 'Bitvavo',
        });
      } else {
        warnings.push(`Bitvavo: Unbekannter Typ "${typRaw}" (Zeile ${row._lineIndex})`);
      }
    } catch (err) {
      failed.push({ line: row._lineIndex, reason: err.message, raw: JSON.stringify(row).slice(0, 80) });
    }
  }

  return result;
}

function parseBitpanda(rows, headers, failed, warnings) {
  const result = [];

  // Neues Format (ab 2025) erkennen: kein 'Cryptocoin Symbol', dafür 'Amount Asset'
  const isNewFormat = !headers.includes('Cryptocoin Symbol') && headers.includes('Amount Asset');

  for (const row of rows) {
    try {
      const rawType = col(row, headers, 'Transaction Type', 'Type') || '';
      const { datum, zeit } = parseDateTime(col(row, headers, 'Timestamp', 'Date'));
      const txId = col(row, headers, 'Transaction ID', 'Transaction id') || '';

      if (!datum) { failed.push({ line: row._lineIndex, reason: 'Kein Datum', raw: rawType }); continue; }

      const typ = mapType(rawType);
      if (typ === 'unknown') {
        warnings.push(`Bitpanda: Unbekannter Typ "${rawType}" (Zeile ${row._lineIndex})`);
        continue;
      }

      const buildBitpandaTx = (coin, amount, eurAmt, fee, feeCoinRaw) => {
        const tx = {
          typ,
          sym: coin,
          datum, zeit,
          menge: Math.abs(amount),
          preis: Math.abs(eurAmt),
          gebuehr: 0,
          stueck: amount !== 0 && eurAmt !== 0 ? Math.abs(eurAmt) / Math.abs(amount) : 0,
          exchange: 'Bitpanda',
          hash: txId,
        };
        // Fee anwenden: in passende Trade-Seite einarbeiten (Fee in coin →
        // menge reduzieren) oder als EUR-Gebühr.
        const absorbed = feeCoinRaw
          ? applyTradingFee(tx, feeCoinRaw, Math.abs(fee))
          : (tx.gebuehr = Math.abs(fee), true);
        // stueck nach möglicher Mengen-Korrektur neu berechnen
        if (tx.menge > 0 && tx.preis > 0) tx.stueck = tx.preis / tx.menge;
        result.push(tx);
        if (feeCoinRaw && !absorbed && Math.abs(fee) > 0) {
          result.push({
            typ: 'gebuehr', sym: normalizeAsset(feeCoinRaw),
            datum, zeit, menge: Math.abs(fee),
            preis: 0, gebuehr: 0, exchange: 'Bitpanda', notiz: 'Trading Fee',
          });
        }
      };

      if (isNewFormat) {
        // Neues Bitpanda-Format (2025+)
        const assetClass = (col(row, headers, 'Asset class') || '').toLowerCase();
        const coin = normalizeAsset(col(row, headers, 'Asset') || '');
        const amount = parseAmount(col(row, headers, 'Amount Asset'));
        const eurAmt = parseAmount(col(row, headers, 'Amount Fiat'));
        const fee = parseAmount(col(row, headers, 'Fee'));
        const feeCoinRaw = col(row, headers, 'Fee asset', 'Fee Asset') || '';

        if (!coin || amount === 0) continue;

        // Reine Fiat-Bewegungen (EUR-Ein-/Auszahlung) sind kein Krypto-Bestands-
        // ereignis, aber ein realer Geldfluss → als eigene Kategorie führen statt
        // verwerfen. `typ` wurde oben aus dem Transaction Type abgeleitet
        // (deposit→einzahlung, withdrawal→auszahlung); Fallback = Vorzeichen.
        if (assetClass === 'fiat') {
          result.push(buildFiatTx(coin, amount, {
            datum, zeit, exchange: 'Bitpanda', notiz: rawType, eurValue: eurAmt, typ,
          }));
          continue;
        }

        buildBitpandaTx(coin, amount, eurAmt, fee, feeCoinRaw);
      } else {
        // Altes Bitpanda-Format
        const coin = normalizeAsset(col(row, headers, 'Cryptocoin Symbol', 'Coin') || '');
        const amount = parseAmount(col(row, headers, 'Amount Cryptocoin', 'Cryptocoin amount'));
        const eurAmt = parseAmount(col(row, headers, 'Amount EUR', 'EUR amount'));
        const fee = parseAmount(col(row, headers, 'Best Fee', 'BEST Fee', 'Fee'));
        const feeCoinRaw = col(row, headers, 'Fee asset', 'Fee Asset') || '';

        if (!coin)  { failed.push({ line: row._lineIndex, reason: 'Kein Coin', raw: rawType }); continue; }
        if (amount === 0) continue;

        buildBitpandaTx(coin, amount, eurAmt, fee, feeCoinRaw);
      }
    } catch (err) {
      failed.push({ line: row._lineIndex, reason: err.message, raw: JSON.stringify(row).slice(0, 80) });
    }
  }

  return result;
}

function parseGemini(rows, headers, failed, warnings) {
  const result = [];

  for (const row of rows) {
    try {
      const sym = normalizeAsset(col(row, headers, 'Symbol') || '');
      const spec = (col(row, headers, 'Specification') || '').toLowerCase();
      const { datum, zeit } = parseDateTime(col(row, headers, 'Date'));
      const usdAmt = parseAmount(col(row, headers, 'USD Amount USD', 'Amount') || '0');
      const qty = parseAmount(col(row, headers, 'Amount', 'Quantity') || '0');
      const fee = parseAmount(col(row, headers, 'Fee (USD) USD', 'Fee') || '0');

      if (!datum || !sym) continue;

      let typ = 'unknown';
      if (spec.includes('buy') || spec.includes('credit'))           typ = 'kauf';
      else if (spec.includes('sell') || spec.includes('debit'))      typ = 'verkauf';
      else if (spec.includes('deposit'))                             typ = 'einzahlung';
      else if (spec.includes('withdrawal') || spec.includes('withdraw')) typ = 'auszahlung';
      else if (spec.includes('reward') || spec.includes('interest')) typ = 'interest';
      else if (spec.includes('airdrop'))                             typ = 'airdrop';

      if (typ === 'unknown') {
        warnings.push(`Gemini: Unbekannte Specification "${spec}" (Zeile ${row._lineIndex})`);
        continue;
      }

      result.push({
        typ,
        sym,
        datum, zeit,
        menge: Math.abs(qty),
        preis: Math.abs(usdAmt),
        gebuehr: Math.abs(fee),
        exchange: 'Gemini',
      });
    } catch (err) {
      failed.push({ line: row._lineIndex, reason: err.message, raw: JSON.stringify(row).slice(0, 80) });
    }
  }

  return result;
}

/**
 * Ledger Live CSV-Parser.
 *
 * Operation Types: IN (Eingang), OUT (Ausgang), FEES (ignoriert), NFT_IN/OUT (ignoriert).
 * OUT + IN desselben Assets-Unterschied innerhalb 60 Minuten → On-Chain Swap.
 */
function parseLedger(rows, headers, failed) {
  const SWAP_WINDOW_MS = 60 * 60 * 1000; // 60 Minuten

  // Eigene-Wallet-Felder (Auto-Registrierung in „Meine Wallets"). Underscore-
  // präfixiert → werden vom handleImportCsv-Mapping NICHT mitgespeichert, dienen
  // nur der einmaligen Adress-Registrierung beim Import.
  const ownFields = (e) => {
    if (!e?.accXpub) return {};
    const chain = ((e.accName || '').trim().split(/\s+/)[0] || 'chain').toLowerCase();
    const label = (e.accName || '').trim().slice(0, 40) || `Ledger ${chain}`;
    // Quelle = Ledger Live → Anbieter fest „Ledger" (Gruppierung in „Meine Wallets";
    // kein Raten). Ein Re-Import backfillt bestehende Ledger-Wallets via ensureOwnWallets.
    return { _ownAddr: e.accXpub, _ownChain: chain, _ownLabel: label, _ownProvider: 'Ledger' };
  };

  // Pass 1: alle gültigen Zeilen normalisieren
  const entries = [];
  for (const row of rows) {
    try {
      const rawType = (col(row, headers, 'Operation Type') || '').trim().toUpperCase();

      // FEES / NFT_* überspringen
      if (!rawType || rawType === 'FEES' || rawType.startsWith('NFT')) continue;
      // REWARD ist eindeutig Staking-Reward (HBAR, ATOM, etc.) — direkt klassifiziert,
      // nicht über die unzuverlässige "kleiner EUR-Wert"-Heuristik.
      const dir =
        rawType === 'IN' ? 'in' :
        rawType === 'OUT' ? 'out' :
        rawType === 'REWARD' ? 'reward' : null;
      if (!dir) continue;

      const sym = normalizeAsset(col(row, headers, 'Currency Ticker') || '');
      let amount = parseAmount(col(row, headers, 'Operation Amount') || '0');
      const fee = parseAmount(col(row, headers, 'Operation Fees') || '0');
      const tsRaw = col(row, headers, 'Operation Date', 'Date') || '';
      const { datum, zeit } = parseDateTime(tsRaw);
      // Hash-Prefix (OUT-abc, IN-abc) entfernen
      const hash = (col(row, headers, 'Operation Hash', 'Transaction Hash') || '')
        .replace(/^(OUT|IN|SEND|RECV|TX)-/i, '')
        .trim();
      // Eigene Wallet-Adresse + Chain (für Auto-Registrierung in „Meine Wallets" →
      // adressbasierte Eigen-Transfer-Erkennung). Ledger Live legt sie als
      // „Account xpub" + „Account Name" (z.B. „Solana Florian") ab. Bei Account-
      // Model-Chains (Solana/EVM) IST der xpub die matchbare On-Chain-Adresse.
      const accXpub = (col(row, headers, 'Account xpub') || '').trim();
      const accName = (col(row, headers, 'Account Name') || '').trim();
      // Gegenwert am Operationsdatum = EUR-Wert beim Zu-/Abfluss → Kostenbasis
      // für Eingänge (Ledger liefert ihn in der CSV, exaktes col() scheitert aber
      // an Header-Varianten → Fuzzy-Fallback). „operation" grenzt sauber von der
      // Export-Gegenwert-Spalte ab.
      const eurValue = parseAmount(
        col(row, headers, 'Countervalue at Operation Date')
        || colFuzzy(row, headers, 'countervalue', 'operation')
        || '0'
      );

      if (!datum || !sym || Math.abs(amount) < 1e-9) continue;

      const ms = new Date(tsRaw).getTime();

      // Dezimal-Fix: manche Ledger-Versionen exportieren Ganzzahl statt Dezimal (438957 statt 438.957)
      const absAmt = Math.abs(amount);
      if (
        Number.isInteger(absAmt) &&
        absAmt > 1000 &&
        eurValue > 0 &&
        eurValue / absAmt < 0.002 &&
        eurValue / (absAmt / 1000) > 0.1
      ) {
        amount = amount / 1000;
      }

      entries.push({ sym, dir, amount: Math.abs(amount), fee, datum, zeit, ms, hash, eurValue, accXpub, accName, _lineIndex: row._lineIndex });
    } catch (err) {
      failed.push({ line: row._lineIndex, reason: err.message, raw: JSON.stringify(row).slice(0, 80) });
    }
  }

  // Hedera/HBAR-Staking: REWARD Hash = '<basis>-staking-reward',
  // zugehöriger OUT Hash = '<basis>' enthält nur die Netzwerkgebühr.
  const rewardBaseHashes = new Set(
    entries
      .filter(e => e.dir === 'reward' && e.hash.endsWith('-staking-reward'))
      .map(e => e.hash.replace(/-staking-reward$/, ''))
  );

  // Pass 2: OUT+IN-Paare innerhalb 60 Minuten → Swap
  // REWARD-Einträge sind immer Staking, kein Swap-Kandidat.
  const matched = new Set();
  const result = [];

  for (let i = 0; i < entries.length; i++) {
    if (matched.has(i) || entries[i].dir !== 'out') continue;
    const out = entries[i];

    // Staking-Gebühr-OUT überspringen (Hedera-Muster)
    if (rewardBaseHashes.has(out.hash) ||
        (out.fee > 0 && out.amount > 0 && out.fee / out.amount > 0.95)) {
      matched.add(i);
      continue;
    }

    // Wert-Plausibilität: einen Swap NUR paaren, wenn die OUT-Seite einen echten
    // EUR-Gegenwert hat. Ohne Gegenwert (wertlose/unbekannte Memecoins wie NAKMAK —
    // Ledger liefert dafür keinen Countervalue) ist ein "OUT + IN innerhalb 60 Min"
    // fast immer ZWEI unabhängige Wallet-Transfers (Coin raus zu eigener Wallet,
    // anderer Coin rein von eigener Wallet), KEIN DEX-Tausch. Solche OUT bleiben
    // auszahlung (Pass 3) und matchen cross-wallet (z.B. mit Phantom). Verhindert
    // den Phantom-Swap "263 NAKMAK → 4 SOL" (echter Florian-Fall 2026-06-23).
    if (!(out.eurValue > 0)) continue;

    let bestJ = -1, bestDiff = Infinity;
    for (let j = 0; j < entries.length; j++) {
      if (matched.has(j) || j === i || entries[j].dir !== 'in') continue;
      const inp = entries[j];
      if (inp.sym === out.sym) continue;       // gleicher Coin → kein Swap
      if (!(inp.eurValue > 0)) continue;        // wertlose IN-Seite → kein Swap-Partner
      const diff = Math.abs(inp.ms - out.ms);
      if (diff <= SWAP_WINDOW_MS && diff < bestDiff) {
        bestDiff = diff;
        bestJ = j;
      }
    }

    if (bestJ >= 0) {
      const inp = entries[bestJ];
      matched.add(i);
      matched.add(bestJ);
      const feeEur =
        out.fee > 0 && out.amount > 0 && out.eurValue > 0
          ? Math.round((out.fee / out.amount) * out.eurValue * 100) / 100
          : 0;
      result.push({
        typ: 'swap',
        symOut: out.sym, mengeOut: out.amount,
        symIn: inp.sym,  mengeIn: inp.amount,
        preis: inp.eurValue || out.eurValue,
        gebuehr: feeEur,
        datum: out.datum, zeit: out.zeit,
        exchange: 'Ledger',
        hash: out.hash || inp.hash,
        ...ownFields(out),
      });
    }
  }

  // Pass 3: ungematchte Zeilen → staking (REWARD) / einzahlung / auszahlung
  for (let i = 0; i < entries.length; i++) {
    if (matched.has(i)) continue;
    const tx = entries[i];
    if (tx.amount < 1e-6) continue; // Dust überspringen

    const feeEur =
      tx.fee > 0 && tx.amount > 0 && tx.eurValue > 0
        ? Math.round((tx.fee / tx.amount) * tx.eurValue * 100) / 100
        : 0;

    if (tx.dir === 'reward') {
      // Ledger-CSV markiert Staking-Rewards explizit als REWARD (HBAR, ATOM, etc.).
      // preis = eurValue zum Empfangs-Zeitpunkt → relevant für § 22 EStG (sonstige Einkünfte).
      result.push({
        typ: 'staking',
        sym: tx.sym,
        datum: tx.datum, zeit: tx.zeit,
        menge: tx.amount,
        preis: tx.eurValue > 0 ? Math.round(tx.eurValue * 100) / 100 : 0,
        gebuehr: feeEur,
        exchange: 'Ledger', hash: tx.hash,
        ...ownFields(tx),
      });
      continue;
    }

    result.push({
      typ: tx.dir === 'in' ? 'einzahlung' : 'auszahlung',
      sym: tx.sym,
      datum: tx.datum, zeit: tx.zeit,
      menge: tx.amount, preis: 0, gebuehr: feeEur,
      exchange: 'Ledger', hash: tx.hash,
      ...(tx.eurValue > 0 ? { countervalue: Math.round(tx.eurValue * 100) / 100 } : {}),
      ...ownFields(tx),
    });
  }

  return result;
}

/**
 * MoonPay trade_history.csv-Parser.
 *
 * Spalten: id, operation, baseCurrency, baseCurrencyAmount, createdAt,
 *          quoteCurrency, quoteCurrencyAmount, feeAmount, extraFeeAmount,
 *          networkFeeAmount, status.
 *
 * Semantik:
 *   buy:  baseCurrency=fiat (EUR) zahlt User → quoteCurrency=crypto erhält User.
 *   sell: baseCurrency=crypto gibt User ab   → quoteCurrency=fiat (EUR) erhält User.
 *
 * baseCurrencyAmount ist der gesamte Brutto-Betrag inkl. aller Gebühren.
 * Für FIFO splitten wir: preis = brutto − fees, gebuehr = Summe der Fees.
 * → fifo.js addiert preis+gebuehr → Anschaffungskosten = Brutto (steuerlich korrekt).
 *
 * Compound-Ticker (ondo_eth, usdc_sol) werden auf das Token reduziert: ONDO, USDC.
 */
function parseMoonPay(rows, headers, failed, warnings) {
  const result = [];

  // MoonPay listet Token-auf-Fremdchain als "{token}_{chain}".
  // Beispiel: "ondo_eth" → ONDO (Token, nicht ETH). Steuerlich relevant ist immer
  // das Token, nicht die Trägerchain.
  const stripChain = (s) => String(s || '').split('_')[0];
  const FIAT = new Set(['eur', 'usd', 'gbp']);

  for (const row of rows) {
    try {
      const status = (col(row, headers, 'status') || '').toLowerCase().trim();
      // Nur abgeschlossene Orders importieren — pending/failed sind keine Steuerereignisse.
      if (status && status !== 'completed') continue;

      const op = (col(row, headers, 'operation') || '').toLowerCase().trim();
      const baseCcy = (col(row, headers, 'baseCurrency') || '').toLowerCase().trim();
      const quoteCcy = (col(row, headers, 'quoteCurrency') || '').toLowerCase().trim();
      const baseAmt = parseAmount(col(row, headers, 'baseCurrencyAmount') || '0');
      const quoteAmt = parseAmount(col(row, headers, 'quoteCurrencyAmount') || '0');
      const fee = parseAmount(col(row, headers, 'feeAmount') || '0');
      const extraFee = parseAmount(col(row, headers, 'extraFeeAmount') || '0');
      const networkFee = parseAmount(col(row, headers, 'networkFeeAmount') || '0');
      const { datum, zeit } = parseDateTime(col(row, headers, 'createdAt', 'Date') || '');
      const id = (col(row, headers, 'id') || '').trim();

      if (!datum || !baseAmt || !quoteAmt) continue;
      if (op !== 'buy' && op !== 'sell') {
        warnings.push(`MoonPay: Unbekannte operation "${op}" (Zeile ${row._lineIndex})`);
        continue;
      }

      const totalFees = fee + extraFee + networkFee;

      if (op === 'buy' && FIAT.has(baseCcy)) {
        const sym = normalizeAsset(stripChain(quoteCcy));
        if (!sym) continue;
        result.push({
          typ: 'kauf',
          sym,
          datum, zeit,
          menge: quoteAmt,
          // Netto-EUR fließt ins Crypto, Brutto ergibt sich aus preis+gebuehr
          preis: Math.max(0, baseAmt - totalFees),
          gebuehr: totalFees,
          stueck: quoteAmt > 0 ? baseAmt / quoteAmt : 0,
          exchange: 'MoonPay',
          hash: id,
          // Nicht-EUR bezahlt (z.B. USD) → später per ECB-Tageskurs auf EUR
          // umrechnen (convertFiatPricesToEur in importCsvFile).
          ...(baseCcy !== 'eur' ? { _fxCcy: baseCcy.toUpperCase() } : {}),
        });
      } else if (op === 'sell' && FIAT.has(quoteCcy)) {
        const sym = normalizeAsset(stripChain(baseCcy));
        if (!sym) continue;
        result.push({
          typ: 'verkauf',
          sym,
          datum, zeit,
          menge: baseAmt,
          // Brutto-Erlös vor Gebühren (FIFO zieht gebuehr für Netto-Proceeds ab)
          preis: quoteAmt + totalFees,
          gebuehr: totalFees,
          stueck: baseAmt > 0 ? (quoteAmt + totalFees) / baseAmt : 0,
          exchange: 'MoonPay',
          hash: id,
          // Erlös nicht in EUR (z.B. USD) → später per ECB-Tageskurs umrechnen.
          ...(quoteCcy !== 'eur' ? { _fxCcy: quoteCcy.toUpperCase() } : {}),
        });
      } else {
        // Crypto-zu-Crypto-Swap (selten bei MoonPay, aber unterstützt)
        result.push({
          typ: 'swap',
          symOut: normalizeAsset(stripChain(baseCcy)),
          mengeOut: baseAmt,
          symIn: normalizeAsset(stripChain(quoteCcy)),
          mengeIn: quoteAmt,
          preis: 0,
          gebuehr: totalFees,
          datum, zeit,
          exchange: 'MoonPay',
          hash: id,
        });
      }
    } catch (err) {
      failed.push({ line: row._lineIndex, reason: err.message, raw: JSON.stringify(row).slice(0, 80) });
    }
  }

  return result;
}

/**
 * Bison (Börse Stuttgart Digital Exchange / BSDEX) transactionhistory.csv-Parser.
 *
 * Header (semikolon-getrennt):
 *   Transaction ID; Transaction type; Currency; Asset; Eur (amount);
 *   Asset (amount); Asset (market price); Fee; Date (UTC …)
 *
 * Semantik:
 *   - "Currency" = Fiat-Seite (Eur), "Asset" = Krypto-Symbol.
 *   - "Eur (amount)" = Fiat-Gesamtbetrag, "Asset (amount)" = Krypto-MENGE,
 *     "Asset (market price)" = Stückpreis (nur Gegenwert/Anzeige).
 *   - Buy  → kauf    (sym=Asset, menge=Asset(amount), preis=Eur(amount))
 *   - Sell → verkauf (sym=Asset, menge=Asset(amount), preis=Eur(amount))
 *   - Deposit  mit Krypto-Asset  → einzahlung  (Transfer-in, z.B. von Ledger)
 *   - Withdraw mit Krypto-Asset  → auszahlung  (Transfer-out zu Wallet)
 *   - Deposit/Withdraw mit Currency=Fiat → Fiat-Ein-/Auszahlung (buildFiatTx,
 *     bestandsneutral, eigene Kategorie).
 *
 * WICHTIG: Der generische Parser scheitert hier komplett — "Date (UTC …)",
 * "Eur (amount)" und "Asset (amount)" matchen keinen seiner exakten
 * Spalten-Kandidaten (jede Zeile → „Kein Datum"). Daher dedizierter Parser.
 * KEINE Dezimal-Auto-Korrektur — Bison liefert exakte Dezimalmengen.
 */
function parseBison(rows, headers, failed, warnings) {
  const result = [];

  for (const row of rows) {
    try {
      const rawTyp   = (col(row, headers, 'Transaction type') || '').toLowerCase().trim();
      const currency = normalizeAsset(col(row, headers, 'Currency') || '');
      const asset    = normalizeAsset(col(row, headers, 'Asset') || '');
      const eurAmt   = parseAmount(col(row, headers, 'Eur (amount)') || '0');
      const assetAmt = parseAmount(col(row, headers, 'Asset (amount)') || '0');
      const mktPrice = parseAmount(col(row, headers, 'Asset (market price)') || '0');
      const fee      = parseAmount(col(row, headers, 'Fee') || '0');
      const { datum, zeit } = parseDateTime(
        col(row, headers, 'Date (UTC - Coordinated Universal Time)', 'Date', 'Date (UTC)') || ''
      );
      const hash = (col(row, headers, 'Transaction ID') || '').trim();

      if (!datum) { failed.push({ line: row._lineIndex, reason: 'Kein Datum', raw: rawTyp }); continue; }

      const countervalue = (assetAmt > 0 && mktPrice > 0)
        ? Math.round(assetAmt * mktPrice * 100) / 100
        : undefined;

      if (rawTyp === 'buy') {
        if (!asset || !(assetAmt > 0)) {
          failed.push({ line: row._lineIndex, reason: 'Buy ohne Asset/Menge', raw: rawTyp });
          continue;
        }
        result.push({
          typ: 'kauf', sym: asset, datum, zeit,
          menge: assetAmt, preis: eurAmt, gebuehr: fee,
          exchange: 'Bison', hash,
        });
      } else if (rawTyp === 'sell') {
        if (!asset || !(assetAmt > 0)) {
          failed.push({ line: row._lineIndex, reason: 'Sell ohne Asset/Menge', raw: rawTyp });
          continue;
        }
        result.push({
          typ: 'verkauf', sym: asset, datum, zeit,
          menge: assetAmt, preis: eurAmt, gebuehr: fee,
          exchange: 'Bison', hash,
        });
      } else if (rawTyp === 'deposit') {
        if (asset && assetAmt > 0 && !FEE_FIAT_SYMS.has(asset)) {
          // Krypto-Eingang (Transfer-in) — z.B. von Ledger eingehend
          result.push({
            typ: 'einzahlung', sym: asset, datum, zeit,
            menge: assetAmt, preis: 0, gebuehr: 0,
            exchange: 'Bison', hash,
            ...(countervalue != null ? { countervalue } : {}),
          });
        } else if (FEE_FIAT_SYMS.has(currency) && eurAmt > 0) {
          // Fiat-Einzahlung (bestandsneutral, eigene Kategorie)
          result.push(buildFiatTx(currency, eurAmt, {
            datum, zeit, exchange: 'Bison', typ: 'einzahlung', eurValue: eurAmt,
          }));
        } else {
          failed.push({ line: row._lineIndex, reason: 'Deposit nicht klassifizierbar', raw: rawTyp });
        }
      } else if (rawTyp === 'withdraw' || rawTyp === 'withdrawal') {
        if (asset && assetAmt > 0 && !FEE_FIAT_SYMS.has(asset)) {
          // Krypto-Ausgang (Transfer-out zu Wallet)
          result.push({
            typ: 'auszahlung', sym: asset, datum, zeit,
            menge: assetAmt, preis: 0, gebuehr: 0,
            exchange: 'Bison', hash,
            ...(countervalue != null ? { countervalue } : {}),
          });
        } else if (FEE_FIAT_SYMS.has(currency) && eurAmt > 0) {
          result.push(buildFiatTx(currency, eurAmt, {
            datum, zeit, exchange: 'Bison', typ: 'auszahlung', eurValue: eurAmt,
          }));
        } else {
          failed.push({ line: row._lineIndex, reason: 'Withdraw nicht klassifizierbar', raw: rawTyp });
        }
      } else {
        warnings.push(`Bison: Unbekannter Typ "${rawTyp}" (Zeile ${row._lineIndex})`);
      }
    } catch (err) {
      failed.push({ line: row._lineIndex, reason: err.message, raw: JSON.stringify(row).slice(0, 80) });
    }
  }

  return result;
}

/**
 * Crypto.com App CSV-Parser.
 *
 * BETA: gegen die dokumentierten Header (BittyTax/Koinly) gebaut, noch NICHT gegen
 * einen echten Nutzer-Export validiert. Emittiert eine Beta-Warnung; Ergebnis prüfen.
 *
 * Header (App-Export):
 *   Timestamp (UTC), Transaction Description, Currency, Amount, To Currency,
 *   To Amount, Native Currency, Native Amount, Native Amount (in USD),
 *   Transaction Kind, Transaction Hash
 *
 * "Transaction Kind" steuert die Klassifikation. "Native Amount" = Fiat-Gegenwert
 * in der App-Währung → dient als preis/Kostenbasis. Interne, bestandsneutrale
 * Bewegungen (Earn-Lock/-Unlock, Supercharger, Fiat-Viban) werden übersprungen,
 * damit kein Phantom-Bestand entsteht.
 */
const CRYPTOCOM_BUY    = new Set(['crypto_purchase', 'viban_purchase', 'recurring_buy_order']);
const CRYPTOCOM_SELL   = new Set(['crypto_viban']);
const CRYPTOCOM_SWAP   = new Set(['crypto_exchange']);
const CRYPTOCOM_DEP    = new Set(['crypto_deposit', 'exchange_to_crypto_transfer']);
const CRYPTOCOM_WD     = new Set(['crypto_withdrawal', 'crypto_to_exchange_transfer']);
const CRYPTOCOM_INCOME = new Set([
  'crypto_earn_interest_paid', 'mco_stake_reward', 'crypto_earn_reward',
  'referral_bonus', 'referral_gift', 'referral_card_cashback', 'transfer_cashback',
  'reimbursement', 'campaign_reward', 'rewards_platform_deposit_credited',
  'admin_wallet_credited', 'supercharger_reward_to_app_credited',
]);
const CRYPTOCOM_IGNORE = new Set([
  'crypto_earn_program_created', 'crypto_earn_program_withdrawn',
  'lockup_lock', 'lockup_unlock', 'supercharger_deposit', 'supercharger_withdrawal',
  // viban_deposit/viban_withdrawal = Fiat-Ein-/Auszahlung (Crypto.com Fiat-Wallet)
  // — NICHT mehr ignorieren, sondern als Fiat-Kategorie führen (siehe unten).
  'crypto_wallet_swap_debited', 'crypto_wallet_swap_credited',
  'dynamic_coin_swap_debited', 'dynamic_coin_swap_credited', 'dust_conversion_debited',
]);

function parseCryptoCom(rows, headers, failed, warnings) {
  const result = [];
  let betaWarned = false;

  for (const row of rows) {
    try {
      const kind = (col(row, headers, 'Transaction Kind') || '').toLowerCase().trim();
      const desc = (col(row, headers, 'Transaction Description') || '').trim();
      const cur  = normalizeAsset(col(row, headers, 'Currency') || '');
      const amt  = parseAmount(col(row, headers, 'Amount') || '0');
      const toCur = normalizeAsset(col(row, headers, 'To Currency') || '');
      const toAmt = parseAmount(col(row, headers, 'To Amount') || '0');
      const nativeAmt = Math.abs(parseAmount(col(row, headers, 'Native Amount') || '0'));
      const { datum, zeit } = parseDateTime(
        col(row, headers, 'Timestamp (UTC)', 'Timestamp(UTC)', 'Timestamp') || ''
      );
      const hash = (col(row, headers, 'Transaction Hash') || '').trim();

      if (!datum) continue;
      if (CRYPTOCOM_IGNORE.has(kind)) continue;       // intern/bestandsneutral

      if (!betaWarned) {
        warnings.push('Crypto.com-Parser ist BETA — bitte importiertes Ergebnis gegen deinen Export prüfen.');
        betaWarned = true;
      }

      // Reine Fiat-Zeile (viban_deposit/withdrawal, EUR-Ein-/Auszahlung) — kein
      // Krypto-Bestandsereignis, aber realer Geldfluss → als eigene Kategorie
      // führen statt verwerfen. Richtung aus dem Vorzeichen von `amt`.
      if (cur && FEE_FIAT_SYMS.has(cur) && !CRYPTOCOM_SWAP.has(kind)) {
        if (Math.abs(amt) === 0) continue;
        const fiatTyp = kind.includes('withdraw') ? 'auszahlung'
          : kind.includes('deposit') ? 'einzahlung'
          : undefined; // sonst: Vorzeichen von amt entscheidet
        result.push(buildFiatTx(cur, amt, {
          datum, zeit, exchange: 'Crypto.com', notiz: desc || kind, eurValue: nativeAmt, typ: fiatTyp,
        }));
        continue;
      }

      // Krypto ↔ Krypto Swap: Currency = abgegeben, To Currency = erhalten
      if (CRYPTOCOM_SWAP.has(kind)) {
        if (!cur || !toCur || Math.abs(amt) === 0 || toAmt === 0) continue;
        result.push({
          typ: 'swap', symOut: cur, mengeOut: Math.abs(amt), symIn: toCur, mengeIn: toAmt,
          preis: nativeAmt, gebuehr: 0, datum, zeit, exchange: 'Crypto.com', hash,
        });
        continue;
      }
      if (!cur || Math.abs(amt) === 0) continue;

      if (CRYPTOCOM_BUY.has(kind)) {
        result.push({
          typ: 'kauf', sym: cur, menge: Math.abs(amt), preis: nativeAmt, gebuehr: 0,
          stueck: Math.abs(amt) > 0 ? nativeAmt / Math.abs(amt) : 0,
          datum, zeit, exchange: 'Crypto.com', hash,
        });
      } else if (CRYPTOCOM_SELL.has(kind)) {
        result.push({
          typ: 'verkauf', sym: cur, menge: Math.abs(amt), preis: nativeAmt, gebuehr: 0,
          stueck: Math.abs(amt) > 0 ? nativeAmt / Math.abs(amt) : 0,
          datum, zeit, exchange: 'Crypto.com', hash,
        });
      } else if (CRYPTOCOM_DEP.has(kind)) {
        result.push({ typ: 'einzahlung', sym: cur, menge: Math.abs(amt), preis: 0, gebuehr: 0, datum, zeit, exchange: 'Crypto.com', hash });
      } else if (CRYPTOCOM_WD.has(kind)) {
        result.push({ typ: 'auszahlung', sym: cur, menge: Math.abs(amt), preis: 0, gebuehr: 0, datum, zeit, exchange: 'Crypto.com', hash });
      } else if (CRYPTOCOM_INCOME.has(kind)) {
        // Einkünfte (Reward/Cashback/Zinsen) → Kostenbasis = Fiat-Gegenwert bei Zufluss
        result.push({ typ: 'staking', sym: cur, menge: Math.abs(amt), preis: nativeAmt, gebuehr: 0, datum, zeit, exchange: 'Crypto.com', hash, notiz: desc });
      } else {
        warnings.push(`Crypto.com: Unbekannter Transaction Kind "${kind}" (Zeile ${row._lineIndex}) — übersprungen.`);
      }
    } catch (err) {
      failed.push({ line: row._lineIndex, reason: err.message, raw: JSON.stringify(row).slice(0, 80) });
    }
  }
  return result;
}

/**
 * KuCoin Spot Orders / Filled Orders CSV-Parser.
 *
 * BETA: gegen dokumentierte Header gebaut, noch nicht gegen echten Export validiert.
 *
 * Header: UID, Account Type, Order ID, Symbol, Side, Order Type, Avg. Filled Price,
 *         Filled Amount, Filled Volume, Filled Volume (USDT), Filled Time(UTC…),
 *         Fee, Fee Currency
 * Symbol = "BASE-QUOTE" (z.B. "BTC-USDT"). Filled Amount = Base-Menge,
 * Filled Volume = Quote-Menge. Quote=Fiat → kauf/verkauf; Quote=Krypto/Stablecoin → swap.
 */
function parseKuCoinSpot(rows, headers, failed, warnings) {
  const result = [];
  let betaWarned = false;
  const timeKey = headers.find((h) => /^filled time/i.test(String(h).trim()));

  for (const row of rows) {
    try {
      const symbol = (col(row, headers, 'Symbol') || '').trim();
      const side   = (col(row, headers, 'Side') || '').toLowerCase().trim();
      const filledAmt = parseAmount(col(row, headers, 'Filled Amount') || '0');  // Base-Menge
      const filledVol = parseAmount(col(row, headers, 'Filled Volume') || '0');  // Quote-Menge
      const fee    = Math.abs(parseAmount(col(row, headers, 'Fee') || '0'));
      const feeCur = (col(row, headers, 'Fee Currency') || '').trim();
      const tsRaw  = timeKey ? row[timeKey] : (col(row, headers, 'Filled Time', 'Time') || '');
      const { datum, zeit } = parseDateTime(tsRaw);
      const orderId = (col(row, headers, 'Order ID') || '').trim();

      if (!datum || !symbol || filledAmt === 0) continue;
      const [baseRaw, quoteRaw] = symbol.split('-');
      const base  = normalizeAsset(baseRaw);
      const quote = normalizeAsset(quoteRaw);
      if (!base || !quote) { warnings.push(`KuCoin: Symbol "${symbol}" nicht parsebar (Zeile ${row._lineIndex})`); continue; }
      if (side !== 'buy' && side !== 'sell') { warnings.push(`KuCoin: Side "${side}" unbekannt (Zeile ${row._lineIndex})`); continue; }

      if (!betaWarned) {
        warnings.push('KuCoin-Parser ist BETA — bitte importiertes Ergebnis gegen deinen Export prüfen.');
        betaWarned = true;
      }

      const quoteIsFiat = FEE_FIAT_SYMS.has(quote);
      let tx;
      if (side === 'buy') {
        tx = quoteIsFiat
          ? { typ: 'kauf', sym: base, menge: filledAmt, preis: filledVol, gebuehr: 0,
              stueck: filledAmt > 0 ? filledVol / filledAmt : 0, datum, zeit, exchange: 'KuCoin', hash: orderId }
          : { typ: 'swap', symOut: quote, mengeOut: filledVol, symIn: base, mengeIn: filledAmt,
              preis: 0, gebuehr: 0, datum, zeit, exchange: 'KuCoin', hash: orderId };
      } else {
        tx = quoteIsFiat
          ? { typ: 'verkauf', sym: base, menge: filledAmt, preis: filledVol, gebuehr: 0,
              stueck: filledAmt > 0 ? filledVol / filledAmt : 0, datum, zeit, exchange: 'KuCoin', hash: orderId }
          : { typ: 'swap', symOut: base, mengeOut: filledAmt, symIn: quote, mengeIn: filledVol,
              preis: 0, gebuehr: 0, datum, zeit, exchange: 'KuCoin', hash: orderId };
      }

      // Fee in Fee Currency: applyTradingFee reduziert/erhöht die Coin-Menge bzw.
      // setzt gebuehr (Fiat). Dritte Coin (z.B. KCS) → separate gebuehr-TX (Lot-Verbrauch).
      const absorbed = applyTradingFee(tx, feeCur, fee);
      result.push(tx);
      if (!absorbed && fee > 0 && feeCur) {
        result.push({ typ: 'gebuehr', sym: normalizeAsset(feeCur), menge: fee, preis: 0, gebuehr: 0, datum, zeit, exchange: 'KuCoin', hash: orderId, notiz: 'Trading-Fee' });
      }
    } catch (err) {
      failed.push({ line: row._lineIndex, reason: err.message, raw: JSON.stringify(row).slice(0, 80) });
    }
  }
  return result;
}

/**
 * KuCoin Deposit-/Withdrawal-History CSV-Parser.
 *
 * BETA. Header: UID, Account Type, Time(UTC…), Coin, Amount, Fee, Hash?,
 *               Deposit Address | Withdrawal Address/Account, Transfer Network,
 *               Status, Remarks. Nur Status == SUCCESS wird importiert.
 * Withdrawal: Netzwerkgebühr verlässt die Wallet zusätzlich → menge = Amount + Fee.
 */
function parseKuCoinTransfer(rows, headers, failed, warnings) {
  const result = [];
  const isWithdrawal = headers.includes('Withdrawal Address/Account');
  const timeKey = headers.find((h) => /^time\(utc/i.test(String(h).trim()));
  let betaWarned = false;

  for (const row of rows) {
    try {
      const status = (col(row, headers, 'Status') || '').toUpperCase().trim();
      if (status && status !== 'SUCCESS') continue;
      const coin = normalizeAsset(col(row, headers, 'Coin') || '');
      const amount = Math.abs(parseAmount(col(row, headers, 'Amount') || '0'));
      const fee = Math.abs(parseAmount(col(row, headers, 'Fee') || '0'));
      const tsRaw = timeKey ? row[timeKey] : (col(row, headers, 'Time') || '');
      const { datum, zeit } = parseDateTime(tsRaw);
      const hash = (col(row, headers, 'Hash') || '').trim();

      if (!datum || !coin || amount === 0) continue;
      if (!betaWarned) {
        warnings.push('KuCoin-Parser ist BETA — bitte importiertes Ergebnis gegen deinen Export prüfen.');
        betaWarned = true;
      }

      const typ = isWithdrawal ? 'auszahlung' : 'einzahlung';
      const menge = typ === 'auszahlung' ? amount + fee : amount; // Withdrawal: Fee verlässt Wallet zusätzlich
      result.push({ typ, sym: coin, menge, preis: 0, gebuehr: 0, datum, zeit, exchange: 'KuCoin', hash });
    } catch (err) {
      failed.push({ line: row._lineIndex, reason: err.message, raw: JSON.stringify(row).slice(0, 80) });
    }
  }
  return result;
}

// Ordnet die Spalten einer (oft handgepflegten) Tabelle EINMAL pro Datei den
// kanonischen Feldern zu: erst exakter Header-Match, dann Fuzzy-Substring für
// Kundentabellen mit Einheiten/Klammern ("Wert (EUR)", "Menge (Coin)",
// "Datum/Zeit", "Coin/Token"). Bereits beanspruchte Spalten werden nicht erneut
// vergeben (verhindert, dass z.B. „Menge" gleichzeitig als Preis landet).
function resolveGenericColumns(headers, CANDIDATES, FUZZY) {
  const map = {};
  const used = new Set();
  const claim = (field) => {
    for (const cand of CANDIDATES[field] || []) {
      const h = headers.find((x) => !used.has(x) && x.toLowerCase().trim() === cand);
      if (h) { map[field] = h; used.add(h); return; }
    }
    for (const n of (FUZZY && FUZZY[field]) || []) {
      const h = headers.find((x) => !used.has(x) && x.toLowerCase().includes(n));
      if (h) { map[field] = h; used.add(h); return; }
    }
  };
  // Reihenfolge = Anspruchspriorität: spezifische/Ziel-Spalten zuerst.
  ['datum', 'typ', 'symTo', 'mengeTo', 'sym', 'menge', 'preis', 'gebuehr', 'hash', 'notiz'].forEach(claim);
  return map;
}

/**
 * Generischer Fallback-Parser: erkennt Felder anhand häufiger Header-Namen heuristisch.
 * Deckt auch handgepflegte Excel-/Numbers-Tabellen ab (deutsche & englische Header,
 * Einheiten in Klammern, Ziel-Coin-Spalte für einzeilige Tausche).
 */
function parseGeneric(rows, headers, failed, warnings) {
  const result = [];

  // Kandidaten-Listen (EXAKTER Header-Match, case-insensitive). Erste Übereinstimmung gewinnt.
  const CANDIDATES = {
    datum:   ['date', 'time', 'datetime', 'timestamp', 'created', 'created_at', 'datum', 'zeit', 'datum/zeit', 'date/time', 'handelsdatum', 'kaufdatum', 'buchungsdatum'],
    typ:     ['type', 'transaction type', 'side', 'operation', 'kind', 'typ', 'art', 'aktion', 'vorgang', 'richtung', 'buchungstyp', 'transaktionstyp', 'transaktion'],
    sym:     ['asset', 'coin', 'currency', 'symbol', 'token', 'base', 'währung', 'waehrung', 'ticker', 'kryptowährung', 'krypto', 'coin/token', 'asset/coin'],
    menge:   ['amount', 'quantity', 'qty', 'size', 'filled', 'change', 'menge', 'betrag', 'anzahl', 'stück', 'stueck', 'coins', 'coin-menge', 'krypto-menge', 'menge (coin)'],
    preis:   ['total', 'value', 'eur', 'usd', 'fiat', 'cost', 'preis', 'wert', 'gesamt', 'gesamtwert', 'wert (eur)', 'wert in eur', 'eur-wert', 'betrag (eur)', 'summe', 'kosten', 'erlös', 'erloes', 'kurswert'],
    gebuehr: ['fee', 'fees', 'commission', 'gebühr', 'gebühren', 'gebuehr', 'gebühr (eur)', 'transaktionsgebühr', 'handelsgebühr', 'provision'],
    hash:    ['txid', 'tx_id', 'transaction_id', 'hash', 'id', 'tx-hash', 'txhash'],
    notiz:   ['note', 'notes', 'comment', 'remark', 'notiz', 'kommentar', 'bemerkung', 'info', 'beschreibung'],
    // Zweites Swap-Bein (Zielcoin/-menge) für einzeilig geloggte Tausche.
    symTo:   ['ziel-coin', 'zielcoin', 'to coin', 'to asset', 'nach coin', 'erhalten coin', 'gekauft coin', 'to', 'nach', 'ziel-asset'],
    mengeTo: ['ziel-menge', 'zielmenge', 'to amount', 'amount to', 'menge nach', 'erhaltene menge', 'menge erhalten', 'ziel-betrag'],
  };
  // Fuzzy-Fallback (Substring) NUR wenn exakt nichts trifft.
  const FUZZY = {
    datum:   ['datum', 'date', 'zeit', 'time'],
    typ:     ['typ', 'transakt', 'vorgang', 'aktion', 'richtung'],
    sym:     ['coin', 'token', 'symbol', 'asset', 'währ', 'waehr', 'ticker', 'krypto'],
    menge:   ['menge', 'anzahl', 'stück', 'stueck', 'coins', 'quantity', 'qty', 'filled'],
    preis:   ['wert', 'betrag', 'gesamt', 'summe', 'kosten', 'erlös', 'erloes', 'eur', 'usd', 'fiat', 'total', 'value'],
    gebuehr: ['gebühr', 'gebuehr', 'fee', 'provision', 'commission'],
    notiz:   ['notiz', 'kommentar', 'bemerkung', 'note', 'comment', 'beschreib', 'info'],
  };
  const colMap = resolveGenericColumns(headers, CANDIDATES, FUZZY);
  const get = (row, field) => (colMap[field] != null ? row[colMap[field]] : undefined);

  for (const row of rows) {
    try {
      const { datum, zeit } = parseDateTime(get(row, 'datum'));
      const rawTyp = get(row, 'typ') || '';
      const sym = normalizeAsset(get(row, 'sym') || '');
      const menge = Math.abs(parseAmount(get(row, 'menge')));
      const preis = Math.abs(parseAmount(get(row, 'preis')));
      const gebuehr = Math.abs(parseAmount(get(row, 'gebuehr')));
      const hash = get(row, 'hash') || '';
      const notiz = get(row, 'notiz') || '';

      if (!datum) { failed.push({ line: row._lineIndex, reason: 'Kein Datum', raw: rawTyp }); continue; }
      if (!sym)   { failed.push({ line: row._lineIndex, reason: 'Kein Symbol', raw: rawTyp }); continue; }
      // Menge 0 NICHT mehr still verschlucken — sonst verschwindet eine
      // versehentliche Nullzeile spurlos (weder failed noch warning).
      if (menge === 0) {
        warnings.push(`Generic: Menge 0 in Zeile ${row._lineIndex} (Typ "${rawTyp}", Coin ${sym}) — übersprungen.`);
        continue;
      }

      const typ = mapType(rawTyp);
      if (typ === 'unknown') {
        warnings.push(`Generic: Unbekannter Typ "${rawTyp}" (Zeile ${row._lineIndex})`);
        continue;
      }

      // Einzeiliger Tausch: braucht ein zweites Bein. Liegt eine Zielcoin-/
      // -menge-Spalte vor → echten zweiseitigen Swap bauen. Fehlt sie, dürfen
      // wir KEINEN beinlosen Swap (coinIn/coinOut=null) erzeugen — der bricht
      // FIFO/Anzeige downstream. Stattdessen klare Warnung + überspringen.
      if (typ === 'swap') {
        const symIn = normalizeAsset(get(row, 'symTo') || '');
        const mengeIn = Math.abs(parseAmount(get(row, 'mengeTo')));
        if (symIn && mengeIn > 0 && symIn !== sym) {
          result.push({
            typ: 'swap', symOut: sym, mengeOut: menge, symIn, mengeIn,
            datum, zeit, preis, gebuehr, exchange: 'Import', hash, notiz,
          });
        } else {
          warnings.push(`Generic: Tausch in Zeile ${row._lineIndex} braucht zwei Coins — Spalten „Coin"+„Ziel-Coin"/„nach" oder zwei Zeilen (Verkauf+Kauf). Übersprungen.`);
        }
        continue;
      }

      result.push({ typ, sym, datum, zeit, menge, preis, gebuehr, exchange: 'Import', hash, notiz });
    } catch (err) {
      failed.push({ line: row._lineIndex, reason: err.message, raw: JSON.stringify(row).slice(0, 80) });
    }
  }

  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// ABSCHNITT 6: Kanonische Normalisierung (sym → coin, Felder vereinheitlichen)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Macht aus einem Roh-TX-Objekt ein vollständig kanonisches Transaktionsobjekt.
 * Setzt sowohl sym* als auch coin*-Felder (beide werden im Rest des Apps erwartet).
 */
export function toCanonical(rawTx) {
  const tx = { ...rawTx };

  // Rohe CSV-Zeile weiterreichen (für ImportReviewScreen, wird nie in Firestore gespeichert)
  if (tx._rawCsvRow != null) { tx.rawCsvRow = tx._rawCsvRow; delete tx._rawCsvRow; }

  // sym → coin (additiv, beide bleiben gesetzt)
  if (tx.coin    == null) tx.coin    = tx.sym    ?? tx.asset ?? null;
  if (tx.coinIn  == null) tx.coinIn  = tx.symIn  ?? null;
  if (tx.coinOut == null) tx.coinOut = tx.symOut ?? null;

  // UPPERCASE + trim für alle Symbol-Felder
  for (const f of ['coin', 'coinIn', 'coinOut', 'sym', 'symIn', 'symOut']) {
    if (tx[f]) tx[f] = String(tx[f]).trim().toUpperCase();
  }

  // Alle Beträge positiv — Ausnahme: preis bei futures_pnl/funding_fee ist vorzeichenbehaftet
  // (negativ=Verlust/Kosten, positiv=Gewinn) und darf nicht absolutiert werden.
  for (const f of ['menge', 'mengeIn', 'mengeOut', 'gebuehr', 'stueck']) {
    if (tx[f] != null) tx[f] = Math.abs(tx[f]);
  }
  if (tx.preis != null && tx.typ !== 'futures_pnl' && tx.typ !== 'funding_fee') {
    tx.preis = Math.abs(tx.preis);
  }

  return tx;
}

// ─────────────────────────────────────────────────────────────────────────────
// ABSCHNITT 7: Duplikat-Filter
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Annotiert TX mit ts / tsPrecision / contentHash und filtert nur ECHTE
 * sekundengenaue Bit-für-Bit-Duplikate. Minuten- oder tagespräzise Kollisionen
 * werden NICHT stillsweg verworfen — der Review-Screen markiert sie als
 * `_collisionGroup`, damit der User pro TX entscheidet.
 *
 * Reason: Zwei Auszahlungen 18:30:00 + 18:30:20 von Binance sind echte 2 TX,
 * dürfen nicht zusammengelegt werden. Zwei Bitvavo-Zeilen ohne Uhrzeit gleichen
 * Inhalts könnten echte 2 TX oder ein Export-Duplikat sein → User entscheidet.
 */
function deduplicateTxs(txs) {
  const annotated = txs.map(tx => annotateTx(tx, { source: 'csv' }));
  const seenSecondHash = new Set();
  const out = [];
  for (const tx of annotated) {
    if (tx.tsPrecision === 'second' && tx.contentHash) {
      if (seenSecondHash.has(tx.contentHash)) continue;
      seenSecondHash.add(tx.contentHash);
    }
    out.push(tx);
  }
  return markCollisions(out);
}

// ─────────────────────────────────────────────────────────────────────────────
// ABSCHNITT 8: Haupt-Einstiegspunkt
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Importiert eine CSV-Datei und gibt normalisierte Transaktionen zurück.
 *
 * @param {File} file  Browser File-Objekt (CSV oder TXT)
 * @returns {Promise<ImportResult>}
 */
export async function importCsvFile(file) {
  const text = await readFileAsText(file);
  return importCsvText(text, file.name);
}

/**
 * Importiert eine vom Kunden angelegte Tabellen-Datei (.xlsx/.xls). Die Datei
 * wird LOKAL (kein KI-Upload) per SheetJS zu CSV-Text gewandelt und durch
 * denselben gehärteten Parser geschickt — so greifen unser Typ-Wissen
 * (TYPE_MAP) und die robuste Spaltenerkennung auch für echte Excel-Dateien.
 * SheetJS wird LAZY geladen (großes Paket → nur bei tatsächlichem Bedarf).
 *
 * @param {File} file  Browser File-Objekt (.xlsx oder .xls)
 * @returns {Promise<ImportResult>}
 */
export async function importSpreadsheetFile(file) {
  const XLSX = await import('xlsx');
  const buf = await file.arrayBuffer();
  // new Uint8Array(...) normalisiert Browser-ArrayBuffer UND Node-Buffer auf das
  // von SheetJS type:'array' erwartete Byte-Array.
  const wb = XLSX.read(new Uint8Array(buf), { type: 'array' });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) {
    return {
      exchange: 'Unknown', txs: [], failed: [], warnings: ['Leere Arbeitsmappe'],
      stats: { totalRows: 0, parsed: 0, skipped: 0, failed: 0 },
    };
  }
  // Erstes Blatt → CSV (Komma). blankrows:false überspringt Leerzeilen.
  const csvText = XLSX.utils.sheet_to_csv(wb.Sheets[sheetName], { blankrows: false });
  const csvName = file.name.replace(/\.(xlsx|xls)$/i, '.csv');
  return importCsvText(csvText, csvName);
}

/**
 * Wie importCsvFile, aber ausgehend von bereits gelesenem CSV-TEXT — für
 * Quellen, die nicht direkt Text sind (z.B. .xlsx → sheet_to_csv).
 *
 * @param {string} text      CSV-Rohtext
 * @param {string} fileName  Original-Dateiname (nur für Logging)
 * @returns {Promise<ImportResult>}
 */
export async function importCsvText(text, fileName = 'import.csv') {
  console.group(`[CSV Import] ${fileName}`);
  console.time('import');

  const warnings = [];
  const failed = [];

  try {
    console.log(`[CSV] Gelesen: ${text.length} Zeichen`);

    // Schritt 2: CSV parsen
    const { headers, rows } = parseCSVText(text);

    if (headers.length === 0) {
      console.warn('[CSV] Keine Headers gefunden');
      console.timeEnd('import');
      console.groupEnd();
      return {
        exchange: 'Unknown', txs: [], failed: [], warnings: ['Leere oder ungültige CSV-Datei'],
        stats: { totalRows: 0, parsed: 0, skipped: 0, failed: 0 },
      };
    }

    console.log(`[CSV] Headers (${headers.length}):`, headers);
    console.log(`[CSV] Datenzeilen: ${rows.length}`);

    // Schritt 3: Exchange erkennen
    const exchange = detectExchange(headers);
    console.log(`[CSV] Exchange erkannt: ${exchange}`);

    if (exchange === 'Generic') {
      warnings.push('Unbekannte Börse — generischer Parser verwendet. Bitte Ergebnis prüfen.');
    }

    // Schritt 4: Exchange-spezifisch parsen
    let rawTxs;
    switch (exchange) {
      case 'Binance':  rawTxs = parseBinance(rows, headers, failed, warnings);  break;
      case 'Kraken':   rawTxs = parseKraken(rows, headers, failed, warnings);   break;
      case 'Coinbase': rawTxs = parseCoinbase(rows, headers, failed, warnings); break;
      case 'BitgetFutures': rawTxs = parseBitgetFutures(rows, headers, failed);   break;
      case 'BitgetSpot':    rawTxs = parseBitgetSpot(rows, headers, failed);     break;
      case 'Bitget':        rawTxs = parseBitget(rows, headers, failed);         break;
      case 'Bitvavo':  rawTxs = parseBitvavo(rows, headers, failed, warnings);  break;
      case 'Bitpanda': rawTxs = parseBitpanda(rows, headers, failed, warnings); break;
      case 'Gemini':   rawTxs = parseGemini(rows, headers, failed, warnings);   break;
      case 'Ledger':   rawTxs = parseLedger(rows, headers, failed);             break;
      case 'MoonPay':  rawTxs = parseMoonPay(rows, headers, failed, warnings);  break;
      case 'Bison':    rawTxs = parseBison(rows, headers, failed, warnings);    break;
      case 'CryptoCom':      rawTxs = parseCryptoCom(rows, headers, failed, warnings);     break;
      case 'KuCoinSpot':     rawTxs = parseKuCoinSpot(rows, headers, failed, warnings);    break;
      case 'KuCoinTransfer': rawTxs = parseKuCoinTransfer(rows, headers, failed, warnings); break;
      default:         rawTxs = parseGeneric(rows, headers, failed, warnings);  break;
    }

    console.log(`[CSV] Roh-Transaktionen: ${rawTxs.length}`);

    // Schritt 5: Kanonisch normalisieren + Import-Batch-ID + Parser-Version
    const batchId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `batch_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const canonical = rawTxs.map(tx => ({
      ...toCanonical(tx),
      importBatchId: batchId,
      parserVersion: PARSER_VERSION,
    }));

    // Schritt 5b: Nicht-EUR-Preise (z.B. MoonPay-USD-Käufe) per ECB-Tageskurs
    // auf EUR umrechnen. Fail-open: ohne Netz bleibt der Betrag + Warnung.
    const fx = await convertFiatPricesToEur(canonical, (m) => warnings.push(m));
    if (fx.converted > 0) console.log(`[CSV] ${fx.converted} Beträge Fiat→EUR umgerechnet`);

    // Schritt 6: Duplikate entfernen
    const txs = deduplicateTxs(canonical);
    const duplicatesRemoved = canonical.length - txs.length;
    if (duplicatesRemoved > 0) {
      warnings.push(`${duplicatesRemoved} doppelte Transaktionen entfernt.`);
      console.log(`[CSV] Duplikate entfernt: ${duplicatesRemoved}`);
    }

    // Schritt 7: Zusammenfassung
    const stats = {
      totalRows: rows.length,
      parsed: txs.length,
      skipped: rows.length - txs.length - failed.length,
      failed: failed.length,
    };

    console.log('[CSV] Ergebnis:', stats);
    if (failed.length > 0) {
      console.warn(`[CSV] ${failed.length} Zeilen fehlgeschlagen:`, failed);
    }
    if (warnings.length > 0) {
      console.warn('[CSV] Warnungen:', warnings);
    }

    // Typ-Verteilung loggen (nützlich für Debugging)
    const typCounts = txs.reduce((acc, tx) => {
      acc[tx.typ] = (acc[tx.typ] || 0) + 1;
      return acc;
    }, {});
    console.log('[CSV] Typen:', typCounts);

    console.timeEnd('import');
    console.groupEnd();

    return { exchange, txs, failed, warnings, stats };
  } catch (err) {
    console.error('[CSV] Kritischer Fehler:', err);
    console.timeEnd('import');
    console.groupEnd();

    return {
      exchange: 'Unknown',
      txs: [],
      failed: [{ line: 0, reason: err.message, raw: '' }],
      warnings: [`Import fehlgeschlagen: ${err.message}`],
      stats: { totalRows: 0, parsed: 0, skipped: 0, failed: 1 },
    };
  }
}

/**
 * Hilfsfunktion: Gibt nur die erkannte Börse und Header zurück (schnell, kein volles Parsen).
 * Nützlich für eine Vorschau bevor der vollständige Import startet.
 *
 * @param {File} file
 * @returns {Promise<{ exchange: string, headers: string[], rowCount: number }>}
 */
export async function previewCsvFile(file) {
  const text = await readFileAsText(file);
  const { headers, rows } = parseCSVText(text);
  const exchange = detectExchange(headers);
  return { exchange, headers, rowCount: rows.length };
}
